import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ChangeDetectorRef } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { Chart, ChartConfiguration, ChartData, registerables } from "chart.js";
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from "rxjs";
import { DashboardService } from "./services/dashboard.service";
import { DashboardCallCenterService } from "../call-center/pages/dashboard/services/dashboard-call-center.service";
import {
  DashboardFiltros,
  TipoPeriodoEnum,
  PipelineProjetosResult,
  ReceitaTemporalResult,
  StatusOrdensCompraResult,
  ProjetosPorStatusResult,
  ProjetosPorFeiraResult,
  ProjetosTemporalResult,
  OrcamentosGraficosResult,
  OrcamentosPorStatusResult,
  OrcamentosPorFeiraResult,
  OrcamentosTemporalResult,
  MargemLucroDistribuicaoResult,
  CustoVsReceitaResult,
  ComposicaoCustosResult,
  PercentuaisResult,
  VariacaoResult,
  ReceitaMetroQuadradoResult,
  OrcamentosEnvioEmailResult,
  OrcamentosPorEventoResult,
  AprovacaoOrcamentoResult,
  ListaContato
} from "./models/dashboard.model";
import { 
  PerformanceListasResult, 
  PerformanceLista,
  CallCenterFiltros as CallCenterFiltrosType,
  TipoPeriodoEnum as CallCenterTipoPeriodoEnum,
  ResumoPerformanceListaResult,
  ResumoPerformanceLista,
  PerformanceFeirasResult,
  PerformanceFeiraResult,
  MensagensPorDiaFeiraResult,
  PerformanceAtendentesResult
} from "../call-center/pages/dashboard/models/dashboard-call-center.model";
import { ToastService } from "src/app/shared/toasts/toast-service";
import { AuthService } from "src/app/core/services/auth/auth.service";
import { EventService } from "../call-center/pages/events/event.service";
import { FeiraModel } from "../call-center/pages/events/model/event.model";

// Registrar todos os componentes do Chart.js
Chart.register(...registerables);

// Interface para próximos eventos (mantida para compatibilidade com o HTML)
interface Transaction {
  nomeFeira: string;
  dataInicioFeira: string;
  dataFimFeira: string;
}

@Component({
  selector: "app-dashboard",
  templateUrl: "./dashboard.component.html",
  styleUrls: ["./dashboard.component.scss"],
})
export class DashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('callCenterComponent') callCenterComponent: any;
  
  breadCrumbItems!: Array<{}>;
  private readonly destroy$ = new Subject<void>();

  // Filtros
  filtroForm!: FormGroup;
  filtros: DashboardFiltros;
  
  // Filtros específicos para Performance por Lista
  filtroPerformanceListasForm!: FormGroup;
  filtrosPerformanceListas: CallCenterFiltrosType;

  // Gráficos
  pipelineChart: Chart<'bar'> | null = null;
  receitaChart: Chart<'line'> | null = null;
  ordensChart: Chart<'pie'> | null = null;
  projetosPorStatusChart: Chart<'pie'> | null = null;
  projetosPorFeiraChart: Chart<'bar'> | null = null;
  projetosTemporalChart: Chart<'line'> | null = null;

  // Gráficos de Orçamentos - Básicos
  orcamentosPorStatusChart: Chart<'pie'> | null = null;
  orcamentosPorFeiraChart: Chart<'bar'> | null = null;
  orcamentosTemporalChart: Chart<'line'> | null = null;

  // Gráficos de Orçamentos - Financeiros
  margemLucroDistribuicaoChart: Chart<'bar'> | null = null;
  custoVsReceitaChart: Chart<'bar' | 'line'> | null = null;
  composicaoCustosChart: Chart<'bar'> | null = null;
  percentuaisChart: Chart<'radar'> | null = null;
  variacaoChart: Chart<'bar'> | null = null;
  receitaMetroQuadradoChart: Chart<'bar'> | null = null;

  // Gráficos de Orçamentos - Processo
  orcamentosEnvioEmailChart: Chart<'line'> | null = null;
  orcamentosPorEventoChart: Chart<'bar'> | null = null;
  aprovacaoOrcamentoChart: Chart<'pie'> | null = null;
  
  // Gráficos de Performance por Lista
  performanceContatosChart: Chart<'bar'> | null = null;
  performanceQualificadosChart: Chart<'bar'> | null = null;
  performanceTaxaQualificacaoChart: Chart<'line'> | null = null;
  performanceMensagensChart: Chart<'bar'> | null = null;
  performanceFeirasChart: Chart<'bar'> | null = null;
  mensagensPorDiaFeiraChart: Chart<'line'> | null = null;
  performanceAtendentesChart: Chart<'bar'> | null = null;
  
  // Dados de Performance por Lista
  performanceListasData: PerformanceListasResult | null = null;
  performanceFeirasData: PerformanceFeirasResult | null = null;
  mensagensPorDiaFeiraData: MensagensPorDiaFeiraResult | null = null;
  performanceAtendentesData: PerformanceAtendentesResult | null = null;
  listaSelecionada: PerformanceLista | null = null;
  
  // Dados de Resumo de Performance por Lista
  resumoPerformanceLista: ResumoPerformanceLista | null = null;
  listaIdParaResumo: string | null = null;
  carregandoResumo = false;

  // Estados
  isLoading = false;
  error = '';
  activeTabId: number = 1; // Aba Call Center é padrão

  // Permissões
  temAcessoAoDashboard: boolean = false;

  // Dados para KPIs de Vendas
  kpiTotalProjetos = 0;
  kpiTotalReceita = 0;
  kpiMediaReceita = 0;
  kpiTotalOrdens = 0;

  // Dados para KPIs de Call Center
  kpiTaxaQualificacao = 0;
  kpiTaxaConversao = 0;
  kpiTotalContatos = 0;
  kpiTotalQualificados = 0;
  kpiTotalMensagens = 0;

  // Meses para select
  meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  // Listas de contatos gerados
  listas: ListaContato[] = [];
  
  // Lista de feiras
  feiras: FeiraModel[] = [];

  // Dados mockados para Transactions
  transactionsAll: Transaction[] = [];
  transactionsBuy: Transaction[] = [];
  transactionsSell: Transaction[] = [];

  constructor(
    private dashboardService: DashboardService,
    private dashboardCallCenterService: DashboardCallCenterService,
    private fb: FormBuilder,
    private toastService: ToastService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private eventService: EventService
  ) {
    const hoje = new Date();
    this.filtros = {
      tipoPeriodo: TipoPeriodoEnum.Mes,
      mes: hoje.getMonth() + 1,
      ano: hoje.getFullYear()
    };

    this.filtroForm = this.fb.group({
      tipoPeriodo: [TipoPeriodoEnum.Mes],
      mes: [hoje.getMonth() + 1],
      ano: [hoje.getFullYear()],
      dataInicio: [null],
      dataFim: [null],
      feiraId: [null],
      clienteId: [null],
      listaId: [null],    // Para Call Center
      atendente: [null]   // Para Call Center
    });
    
    // Formulário de filtros específico para Performance por Lista
    this.filtrosPerformanceListas = {
      tipoPeriodo: CallCenterTipoPeriodoEnum.Mes,
      mes: hoje.getMonth() + 1,
      ano: hoje.getFullYear(),
      dataInicio: undefined,
      dataFim: undefined,
      feiraId: undefined,
      listaId: undefined,
      atendente: undefined
    };
    
    this.filtroPerformanceListasForm = this.fb.group({
      tipoPeriodo: [CallCenterTipoPeriodoEnum.Mes],
      mes: [hoje.getMonth() + 1],
      ano: [hoje.getFullYear()],
      dataInicio: [null as string | null],
      dataFim: [null as string | null],
      feiraId: [null as string | null],
      listaId: [null as string | null],
      atendente: [null as string | null]
    });
  }

  ngOnInit(): void {
    this.breadCrumbItems = [
      { label: "Dashboard" },
      { label: "Dashboard", active: true },
    ];

    // Verificar permissões
    this.verificarPermissoes();

    // Carregar próximos eventos
    this.carregarProximosEventos();

    // Carregar listas de contatos gerados
    this.carregarListas();

    // Carregar todos os KPIs imediatamente (independente da aba)
    // Usar setTimeout para garantir que os filtros estejam completamente inicializados
    setTimeout(() => {
      this.carregarTodosKpis();
    }, 100);

    // Carregar gráficos de Performance por Lista automaticamente com filtros padrão
    setTimeout(() => {
      this.carregarPerformanceListas();
      this.carregarMensagensPorDiaFeira();
      this.carregarPerformanceAtendentes();
    }, 2000);

    // Configurar observação de mudanças nos filtros
    this.filtroForm.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.aplicarFiltros();
      });

    // Carregar dados iniciais da aba padrão (Call Center)
    // Os gráficos serão carregados quando a aba for ativada
  }

  carregarListas(): void {
    this.dashboardService.consultarListasDeContatosGerados().subscribe({
      next: (response) => {
        this.listas = response.listas || [];
        
        // Selecionar automaticamente a primeira lista se houver listas e nenhuma estiver selecionada
        if (this.listas.length > 0 && !this.listaIdParaResumo) {
          this.listaIdParaResumo = this.listas[0].id;
          // Carregar o resumo da primeira lista automaticamente
          this.carregarResumoPerformanceLista();
        }
      },
      error: (error) => {
        console.error('Erro ao carregar listas:', error);
        this.listas = [];
      }
    });
  }

  carregarFeiras(): void {
    this.eventService.consultarFeira({
      nome: '',
      paginaAtual: '1',
      quantidadePorPagina: '100'
    }).subscribe({
      next: (response) => {
        this.feiras = response.feiras || [];
      },
      error: (error) => {
        console.error('Erro ao carregar feiras:', error);
        this.feiras = [];
      }
    });
  }

  ngAfterViewInit(): void {
    // Carregar gráficos da aba padrão (Call Center) ao inicializar
    setTimeout(() => {
      this.carregarGraficosAbaAtiva();
      // Aguardar um pouco mais para garantir que os KPIs do Call Center sejam atualizados
      setTimeout(() => {
        if (this.callCenterComponent) {
          this.kpiTaxaQualificacao = this.callCenterComponent.kpiTaxaQualificacao || 0;
          this.kpiTaxaConversao = this.callCenterComponent.kpiTaxaConversao || 0;
          this.kpiTotalContatos = this.callCenterComponent.kpiTotalContatos || 0;
          this.kpiTotalQualificados = this.callCenterComponent.kpiTotalQualificados || 0;
          this.kpiTotalMensagens = this.callCenterComponent.kpiTotalMensagens || 0;
          // Não sobrescrever kpiTotalProjetos aqui, pois ele já foi carregado diretamente do endpoint
          // this.kpiTotalProjetos = this.callCenterComponent.kpiTotalProjetos || 0;
        }
      }, 1000);
    }, 100);
  }

  onTabChange(tabId: number | null): void {
    if (tabId) {
      this.activeTabId = tabId;
      // Recarregar gráficos da aba ativa com os filtros atuais
      setTimeout(() => {
        this.carregarGraficosAbaAtiva();
      }, 100);
    }
  }

  carregarGraficosAbaAtiva(): void {
    if (this.activeTabId === 1) {
      // Aba Call Center - aguardar um pouco para garantir que os canvas estejam no DOM
      setTimeout(() => {
        if (this.callCenterComponent) {
          if (this.callCenterComponent.filtrosExternos) {
            this.callCenterComponent.converterFiltrosExternos();
          }
          this.callCenterComponent.carregarGraficos();
          // Atualizar KPIs do Call Center após carregar (aguardar dados serem processados)
          setTimeout(() => {
            if (this.callCenterComponent) {
              this.kpiTaxaQualificacao = this.callCenterComponent.kpiTaxaQualificacao || 0;
              this.kpiTaxaConversao = this.callCenterComponent.kpiTaxaConversao || 0;
              this.kpiTotalContatos = this.callCenterComponent.kpiTotalContatos || 0;
              this.kpiTotalQualificados = this.callCenterComponent.kpiTotalQualificados || 0;
              this.kpiTotalMensagens = this.callCenterComponent.kpiTotalMensagens || 0;
              // Não sobrescrever kpiTotalProjetos aqui, pois ele já foi carregado diretamente do endpoint
              // this.kpiTotalProjetos = this.callCenterComponent.kpiTotalProjetos || 0;
            }
          }, 1500);
        }
        // Carregar gráficos de Performance por Lista independentemente do callCenterComponent
        setTimeout(() => {
          this.carregarPerformanceListas();
        }, 2000);
      }, 500);
    } else if (this.activeTabId === 2) {
      // Aba Projetos - aguardar um pouco para garantir que os canvas estejam no DOM
      setTimeout(() => {
        this.carregarGraficos();
      }, 200);
    } else if (this.activeTabId === 3) {
      // Aba Financeiro - aguardar um pouco para garantir que os canvas estejam no DOM
      setTimeout(() => {
        this.carregarGraficosOrcamentos();
      }, 200);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.destruirGraficos();
  }

  aplicarFiltros(): void {
    const formValue = this.filtroForm.value;
    
    // Criar novo objeto de filtros
    const novosFiltros: DashboardFiltros = {
      tipoPeriodo: formValue.tipoPeriodo,
      mes: formValue.tipoPeriodo === TipoPeriodoEnum.Mes ? formValue.mes : undefined,
      ano: formValue.ano,
      dataInicio: formValue.tipoPeriodo === TipoPeriodoEnum.Personalizado ? formValue.dataInicio : undefined,
      dataFim: formValue.tipoPeriodo === TipoPeriodoEnum.Personalizado ? formValue.dataFim : undefined,
      feiraId: formValue.feiraId || undefined,
      clienteId: formValue.clienteId || undefined,
      listaId: formValue.listaId || undefined,    // Para Call Center
      atendente: formValue.atendente || undefined // Para Call Center
    };

    // Verificar se os filtros realmente mudaram antes de atualizar
    const filtrosMudaram = !this.filtros || 
      this.filtros.tipoPeriodo !== novosFiltros.tipoPeriodo ||
      this.filtros.mes !== novosFiltros.mes ||
      this.filtros.ano !== novosFiltros.ano ||
      this.filtros.dataInicio !== novosFiltros.dataInicio ||
      this.filtros.dataFim !== novosFiltros.dataFim ||
      this.filtros.feiraId !== novosFiltros.feiraId ||
      this.filtros.clienteId !== novosFiltros.clienteId ||
      this.filtros.listaId !== novosFiltros.listaId ||
      this.filtros.atendente !== novosFiltros.atendente;

    // Atualizar filtros apenas se mudaram
    if (filtrosMudaram) {
      this.filtros = novosFiltros;
      
      // Recarregar KPI de Total de Projetos sempre que os filtros mudarem
      this.carregarKpiTotalProjetos();
      
      // Recarregar todos os KPIs quando os filtros mudarem
      this.carregarTodosKpis();
      
      // Recarregar gráficos da aba ativa com os novos filtros
      this.carregarGraficosAbaAtiva();
    }
  }

  /**
   * Converte DashboardFiltros para CallCenterFiltros
   */
  private converterParaCallCenterFiltros(filtros: DashboardFiltros): CallCenterFiltrosType {
    return {
      tipoPeriodo: filtros.tipoPeriodo,
      mes: filtros.mes,
      ano: filtros.ano,
      dataInicio: filtros.dataInicio,
      dataFim: filtros.dataFim,
      feiraId: filtros.feiraId,
      listaId: filtros.listaId,
      atendente: filtros.atendente
    };
  }

  /**
   * Carrega todos os 4 KPIs imediatamente, respeitando os filtros
   */
  carregarTodosKpis(): void {
    const callCenterFiltros = this.converterParaCallCenterFiltros(this.filtros);

    // 1. Carregar KPI Total de Qualificados
    this.carregarKpiTotalQualificados(callCenterFiltros);

    // 2. Carregar KPI Total de Contatos
    this.carregarKpiTotalContatos(callCenterFiltros);

    // 3. Carregar KPI Total de Projetos
    this.carregarKpiTotalProjetos();

    // 4. Carregar KPI Total de Receita
    this.carregarKpiTotalReceita();
  }

  carregarKpiTotalQualificados(filtros: CallCenterFiltrosType): void {
    this.dashboardCallCenterService.consultarTaxaConversaoProjetos(filtros).subscribe({
      next: (result) => {
        if (result && result.totalQualificados !== undefined && result.totalQualificados !== null) {
          this.kpiTotalQualificados = result.totalQualificados;
        }
      },
      error: (error) => {
        console.error('Erro ao carregar KPI Total de Qualificados:', error);
      }
    });
  }

  carregarKpiTotalContatos(filtros: CallCenterFiltrosType): void {
    this.dashboardCallCenterService.consultarTaxaQualificacao(filtros).subscribe({
      next: (result) => {
        if (result && result.totalContatos !== undefined && result.totalContatos !== null) {
          this.kpiTotalContatos = result.totalContatos;
        }
      },
      error: (error) => {
        console.error('Erro ao carregar KPI Total de Contatos:', error);
      }
    });
  }

  carregarKpiTotalProjetos(): void {
    // Carregar apenas o KPI de Total de Projetos usando o endpoint agregado /graficos
    // Este é o mesmo endpoint usado na aba Projetos, que retorna pipelineProjetos.totalProjetos
    this.dashboardService.consultarTodosGraficos(this.filtros).subscribe({
      next: (result) => {
        console.log('Resultado do endpoint /graficos:', result);
        if (result && result.pipelineProjetos && result.pipelineProjetos.totalProjetos !== undefined && result.pipelineProjetos.totalProjetos !== null) {
          this.kpiTotalProjetos = result.pipelineProjetos.totalProjetos;
          console.log('KPI Total de Projetos atualizado para:', this.kpiTotalProjetos);
        } else {
          console.warn('Resultado do endpoint /graficos não contém pipelineProjetos.totalProjetos válido:', result);
        }
      },
      error: (error) => {
        console.error('Erro ao carregar KPI Total de Projetos:', error);
        console.error('Detalhes do erro:', {
          status: error.status,
          message: error.message,
          error: error.error
        });
      }
    });
  }

  carregarKpiTotalReceita(): void {
    // Carregar apenas o KPI de Total de Receita usando o endpoint de receita temporal
    this.dashboardService.consultarReceitaTemporal(this.filtros).subscribe({
      next: (result) => {
        if (result && result.totalReceita !== undefined && result.totalReceita !== null) {
          this.kpiTotalReceita = result.totalReceita;
        }
      },
      error: (error) => {
        console.error('Erro ao carregar KPI Total de Receita:', error);
      }
    });
  }

  carregarGraficos(): void {
    this.isLoading = true;
    this.error = '';

    this.dashboardService.consultarTodosGraficos(this.filtros).subscribe({
      next: (result) => {
        this.isLoading = false;
        
        // Aguardar um pouco para garantir que os canvas estejam no DOM após o *ngIf
        setTimeout(() => {
          // Atualizar KPIs
          if (result.pipelineProjetos) {
            this.kpiTotalProjetos = result.pipelineProjetos.totalProjetos;
            this.criarGraficoPipeline(result.pipelineProjetos);
          }
          if (result.receitaTemporal) {
            this.kpiTotalReceita = result.receitaTemporal.totalReceita;
            this.kpiMediaReceita = result.receitaTemporal.mediaReceita;
            this.criarGraficoReceita(result.receitaTemporal);
          }
          if (result.projetosPorStatus) {
            this.criarGraficoProjetosPorStatus(result.projetosPorStatus);
          }
          if (result.statusOrdensCompra) {
            this.kpiTotalOrdens = result.statusOrdensCompra.totalOrdens;
            this.criarGraficoOrdens(result.statusOrdensCompra);
          }
          if (result.projetosPorFeira) {
            this.criarGraficoProjetosPorFeira(result.projetosPorFeira);
          }
          if (result.projetosTemporal) {
            this.criarGraficoProjetosTemporal(result.projetosTemporal);
          }
        }, 200);
      },
      error: (error) => {
        console.error('Erro ao carregar gráficos:', error);
        this.error = 'Erro ao carregar os gráficos. Tente novamente.';
        this.isLoading = false;
        this.toastService.show('Erro ao carregar gráficos do dashboard', {
          classname: 'bg-danger text-light',
        });
      }
    });
  }

  carregarGraficosOrcamentos(): void {
    this.isLoading = true;
    this.error = '';

    // Endpoint agregado retorna TODOS os 12 gráficos de uma vez
    this.dashboardService.consultarTodosOrcamentosGraficos(this.filtros).subscribe({
      next: (result) => {
        this.isLoading = false;
        
        // Aguardar um pouco para garantir que os canvas estejam no DOM após o *ngIf
        setTimeout(() => {
          // Gráficos de Processo (6 gráficos básicos)
          if (result.orcamentosPorStatus) {
            this.criarGraficoOrcamentosPorStatus(result.orcamentosPorStatus);
          }
          if (result.orcamentosPorFeira) {
            this.criarGraficoOrcamentosPorFeira(result.orcamentosPorFeira);
          }
          if (result.orcamentosTemporal) {
            this.criarGraficoOrcamentosTemporal(result.orcamentosTemporal);
          }
          if (result.orcamentosEnvioEmail) {
            this.criarGraficoOrcamentosEnvioEmail(result.orcamentosEnvioEmail);
          }
          if (result.orcamentosPorEvento) {
            this.criarGraficoOrcamentosPorEvento(result.orcamentosPorEvento);
          }
          if (result.aprovacaoOrcamento) {
            this.criarGraficoAprovacaoOrcamento(result.aprovacaoOrcamento);
          }
          
          // Gráficos Financeiros (6 gráficos)
          if (result.margemLucroDistribuicao) {
            this.criarGraficoMargemLucroDistribuicao(result.margemLucroDistribuicao);
          }
          if (result.custoVsReceita) {
            this.criarGraficoCustoVsReceita(result.custoVsReceita);
          }
          if (result.composicaoCustos) {
            this.criarGraficoComposicaoCustos(result.composicaoCustos);
          }
          if (result.percentuais) {
            this.criarGraficoPercentuais(result.percentuais);
          }
          if (result.variacao) {
            this.criarGraficoVariacao(result.variacao);
          }
          if (result.receitaMetroQuadrado) {
            this.criarGraficoReceitaMetroQuadrado(result.receitaMetroQuadrado);
          }
        }, 200);
      },
      error: (error) => {
        console.error('Erro ao carregar gráficos de orçamentos:', error);
        this.error = 'Erro ao carregar os gráficos de orçamentos. Tente novamente.';
        this.isLoading = false;
        this.toastService.show('Erro ao carregar gráficos de orçamentos', {
          classname: 'bg-danger text-light',
        });
      }
    });
  }

  destruirGraficos(): void {
    if (this.pipelineChart) {
      this.pipelineChart.destroy();
      this.pipelineChart = null;
    }
    if (this.receitaChart) {
      this.receitaChart.destroy();
      this.receitaChart = null;
    }
    if (this.ordensChart) {
      this.ordensChart.destroy();
      this.ordensChart = null;
    }
    if (this.projetosPorStatusChart) {
      this.projetosPorStatusChart.destroy();
      this.projetosPorStatusChart = null;
    }
    if (this.projetosPorFeiraChart) {
      this.projetosPorFeiraChart.destroy();
      this.projetosPorFeiraChart = null;
    }
    if (this.projetosTemporalChart) {
      this.projetosTemporalChart.destroy();
      this.projetosTemporalChart = null;
    }
    // Gráficos de Orçamentos
    if (this.orcamentosPorStatusChart) {
      this.orcamentosPorStatusChart.destroy();
      this.orcamentosPorStatusChart = null;
    }
    if (this.orcamentosPorFeiraChart) {
      this.orcamentosPorFeiraChart.destroy();
      this.orcamentosPorFeiraChart = null;
    }
    if (this.orcamentosTemporalChart) {
      this.orcamentosTemporalChart.destroy();
      this.orcamentosTemporalChart = null;
    }
    if (this.margemLucroDistribuicaoChart) {
      this.margemLucroDistribuicaoChart.destroy();
      this.margemLucroDistribuicaoChart = null;
    }
    if (this.custoVsReceitaChart) {
      this.custoVsReceitaChart.destroy();
      this.custoVsReceitaChart = null;
    }
    if (this.composicaoCustosChart) {
      this.composicaoCustosChart.destroy();
      this.composicaoCustosChart = null;
    }
    if (this.percentuaisChart) {
      this.percentuaisChart.destroy();
      this.percentuaisChart = null;
    }
    if (this.variacaoChart) {
      this.variacaoChart.destroy();
      this.variacaoChart = null;
    }
    if (this.receitaMetroQuadradoChart) {
      this.receitaMetroQuadradoChart.destroy();
      this.receitaMetroQuadradoChart = null;
    }
    if (this.orcamentosEnvioEmailChart) {
      this.orcamentosEnvioEmailChart.destroy();
      this.orcamentosEnvioEmailChart = null;
    }
    if (this.orcamentosPorEventoChart) {
      this.orcamentosPorEventoChart.destroy();
      this.orcamentosPorEventoChart = null;
    }
    if (this.aprovacaoOrcamentoChart) {
      this.aprovacaoOrcamentoChart.destroy();
      this.aprovacaoOrcamentoChart = null;
    }
    if (this.performanceContatosChart) {
      this.performanceContatosChart.destroy();
      this.performanceContatosChart = null;
    }
    if (this.performanceQualificadosChart) {
      this.performanceQualificadosChart.destroy();
      this.performanceQualificadosChart = null;
    }
    if (this.performanceTaxaQualificacaoChart) {
      this.performanceTaxaQualificacaoChart.destroy();
      this.performanceTaxaQualificacaoChart = null;
    }
    if (this.performanceMensagensChart) {
      this.performanceMensagensChart.destroy();
      this.performanceMensagensChart = null;
    }
    if (this.performanceFeirasChart) {
      this.performanceFeirasChart.destroy();
      this.performanceFeirasChart = null;
    }
    if (this.mensagensPorDiaFeiraChart) {
      this.mensagensPorDiaFeiraChart.destroy();
      this.mensagensPorDiaFeiraChart = null;
    }
    if (this.performanceAtendentesChart) {
      this.performanceAtendentesChart.destroy();
      this.performanceAtendentesChart = null;
    }
  }
  
  converterFiltrosParaCallCenter(): CallCenterFiltrosType {
    return {
      tipoPeriodo: this.filtros.tipoPeriodo as CallCenterTipoPeriodoEnum,
      mes: this.filtros.mes,
      ano: this.filtros.ano,
      dataInicio: this.filtros.dataInicio,
      dataFim: this.filtros.dataFim,
      feiraId: this.filtros.feiraId,
      listaId: this.filtros.listaId,
      atendente: this.filtros.atendente
    };
  }
  
  aplicarFiltrosPerformanceListas(): void {
    const formValue = this.filtroPerformanceListasForm.value;
    this.filtrosPerformanceListas = {
      tipoPeriodo: formValue.tipoPeriodo,
      mes: formValue.mes,
      ano: formValue.ano,
      dataInicio: formValue.dataInicio || undefined,
      dataFim: formValue.dataFim || undefined,
      feiraId: formValue.feiraId || undefined,
      listaId: formValue.listaId || undefined,
      atendente: formValue.atendente || undefined
    };
    this.carregarPerformanceListas();
    this.carregarPerformanceFeiras();
    this.carregarMensagensPorDiaFeira();
    this.carregarPerformanceAtendentes();
  }
  
  carregarPerformanceListas(): void {
    // Usar filtros padrão (mês e ano atual) se não houver filtros aplicados
    const hoje = new Date();
    const filtrosParaUsar = {
      tipoPeriodo: this.filtrosPerformanceListas?.tipoPeriodo || CallCenterTipoPeriodoEnum.Mes,
      mes: this.filtrosPerformanceListas?.mes || hoje.getMonth() + 1,
      ano: this.filtrosPerformanceListas?.ano || hoje.getFullYear(),
      dataInicio: this.filtrosPerformanceListas?.dataInicio || undefined,
      dataFim: this.filtrosPerformanceListas?.dataFim || undefined,
      feiraId: this.filtrosPerformanceListas?.feiraId || undefined,
      listaId: this.filtrosPerformanceListas?.listaId || undefined,
      atendente: this.filtrosPerformanceListas?.atendente || undefined
    };

    this.dashboardCallCenterService.consultarPerformanceListas(filtrosParaUsar).subscribe({
      next: (result) => {
        console.log('Dados recebidos da API:', result);
        this.performanceListasData = result;
        this.tentativasCriarGraficos = 0; // Reset contador
        
        // Forçar detecção de mudanças para renderizar os canvas
        this.cdr.detectChanges();
        
        // Aguardar para o Angular renderizar os canvas após o *ngIf
        // Aumentar o timeout para garantir que o DOM foi atualizado
        setTimeout(() => {
          console.log('Tentando criar gráficos após receber dados...');
          this.criarGraficosPerformanceListas();
        }, 800);
      },
      error: (error) => {
        console.error('Erro ao carregar performance de listas:', error);
        this.performanceListasData = null;
        this.tentativasCriarGraficos = 0;
      }
    });

    // Carregar dados de Performance por Feiras
    this.carregarPerformanceFeiras();
    
    // Carregar dados de Mensagens por Dia e Feira
    this.carregarMensagensPorDiaFeira();
    
    // Carregar dados de Performance de Atendentes
    this.carregarPerformanceAtendentes();
  }
  
  private tentativasCriarGraficos = 0;
  private readonly MAX_TENTATIVAS = 10;
  
  // Contadores de tentativas para gráficos específicos
  private tentativasCriarPerformanceFeiras = 0;
  private tentativasCriarMensagensPorDiaFeira = 0;
  private tentativasCriarPerformanceAtendentes = 0;

  criarGraficosPerformanceListas(): void {
    if (!this.performanceListasData || !this.performanceListasData.listas || this.performanceListasData.listas.length === 0) {
      return;
    }
    
    // Verificar se os canvas obrigatórios existem no DOM
    // Gráficos ocultos: performanceContatosChart, performanceQualificadosChart
    const canvasIdsObrigatorios: string[] = [];
    
    // Se não houver canvas obrigatórios, pular a verificação
    if (canvasIdsObrigatorios.length > 0) {
      const missingCanvas = canvasIdsObrigatorios.filter(id => document.getElementById(id) === null);
      
      if (missingCanvas.length > 0) {
        this.tentativasCriarGraficos++;
        console.log(`criarGraficosPerformanceListas: Canvas obrigatórios não encontrados (tentativa ${this.tentativasCriarGraficos}/${this.MAX_TENTATIVAS}):`, missingCanvas);
        
        if (this.tentativasCriarGraficos >= this.MAX_TENTATIVAS) {
          console.error('criarGraficosPerformanceListas: Número máximo de tentativas atingido. Canvas obrigatórios não foram encontrados.');
          this.tentativasCriarGraficos = 0;
          return;
        }
        
        // Tentar novamente após um delay
        setTimeout(() => {
          this.criarGraficosPerformanceListas();
        }, 500);
        return;
      }
    }
    
    console.log('criarGraficosPerformanceListas: Criando gráficos disponíveis...');
    this.tentativasCriarGraficos = 0;
    
    // Aguardar um pouco mais para garantir que o Angular renderizou os canvas após o *ngIf
    setTimeout(() => {
      // Gráficos ocultos - não criar mais
      // this.criarGraficoPerformanceContatos();
      // this.criarGraficoPerformanceQualificados();
      
      // Criar gráfico de mensagens apenas se o canvas existir (opcional)
      if (document.getElementById('performanceMensagensChart')) {
        this.criarGraficoPerformanceMensagens();
      }
    }, 100);
  }

  carregarPerformanceFeiras(): void {
    // Usar filtros padrão (mês e ano atual) se não houver filtros aplicados
    const hoje = new Date();
    const filtrosParaUsar = {
      tipoPeriodo: this.filtrosPerformanceListas?.tipoPeriodo || CallCenterTipoPeriodoEnum.Mes,
      mes: this.filtrosPerformanceListas?.mes || hoje.getMonth() + 1,
      ano: this.filtrosPerformanceListas?.ano || hoje.getFullYear(),
      dataInicio: this.filtrosPerformanceListas?.dataInicio || undefined,
      dataFim: this.filtrosPerformanceListas?.dataFim || undefined,
      feiraId: this.filtrosPerformanceListas?.feiraId || undefined,
      listaId: this.filtrosPerformanceListas?.listaId || undefined,
      atendente: this.filtrosPerformanceListas?.atendente || undefined
    };

    this.dashboardCallCenterService.consultarPerformanceFeiras(filtrosParaUsar).subscribe({
      next: (result) => {
        console.log('Dados de Performance por Feiras recebidos:', result);
        this.performanceFeirasData = result;
        
        // Forçar detecção de mudanças para renderizar o canvas
        this.cdr.detectChanges();
        
        // Resetar contador de tentativas
        this.tentativasCriarPerformanceFeiras = 0;
        
        // Aguardar para o Angular renderizar o canvas após o *ngIf e tentar criar o gráfico
        setTimeout(() => {
          this.tentarCriarGraficoPerformanceFeiras();
        }, 100);
      },
      error: (error) => {
        console.error('Erro ao carregar performance por feiras:', error);
        this.performanceFeirasData = null;
      }
    });
  }
  
  criarGraficoPerformanceContatos(): void {
    const ctx = document.getElementById('performanceContatosChart') as HTMLCanvasElement;
    if (!ctx) {
      console.error('Canvas performanceContatosChart não encontrado no DOM');
      return;
    }
    if (!this.performanceListasData || !this.performanceListasData.listas) {
      console.error('Dados não disponíveis para criar gráfico de contatos');
      return;
    }
    
    console.log('Criando gráfico de contatos com dados:', this.performanceListasData.listas);
    
    // Destruir gráfico existente se houver
    if (this.performanceContatosChart) {
      this.performanceContatosChart.destroy();
      this.performanceContatosChart = null;
    }
    
    // Verificar se há um gráfico Chart.js registrado no canvas e destruí-lo
    const existingChart = Chart.getChart(ctx);
    if (existingChart) {
      existingChart.destroy();
    }
    
    const chartData: ChartData<'bar'> = {
      labels: this.performanceListasData.listas.map((l: PerformanceLista) => l.nomeLista),
      datasets: [{
        label: 'Total de Contatos',
        data: this.performanceListasData.listas.map((l: PerformanceLista) => l.totalContatos),
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    };
    
    // Plugin para rótulos dentro das barras
    const datalabelsPluginContatos = {
      id: 'datalabelsPerformanceContatos',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        meta.data.forEach((bar: any, index: number) => {
          if (!bar) return;
          
          const value = chart.data.datasets[0].data[index] as number;
          
          // Obter coordenadas da barra
          const x = bar.x;
          const y = bar.y;
          const base = (chart as any).scales.y.getPixelForValue(0);
          const height = Math.abs(y - base);
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Só exibir rótulo se a barra tiver altura suficiente
          if (height > 15) {
            // Posicionar no meio da barra (y é o topo, então y - height/2 é o centro)
            const labelY = y < base ? y + height / 2 : y - height / 2;
            ctx.fillText(value.toString(), x, labelY);
          }
        });
        
        ctx.restore();
      }
    };
    
    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [datalabelsPluginContatos],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          title: {
            display: true,
            text: 'Total de Contatos por Lista'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `Contatos: ${context.parsed.y}`;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            }
          }
        }
      }
    };
    
    try {
      this.performanceContatosChart = new Chart(ctx, config);
      console.log('Gráfico de contatos criado com sucesso');
    } catch (error) {
      console.error('Erro ao criar gráfico de contatos:', error);
    }
  }
  
  criarGraficoPerformanceQualificados(): void {
    const ctx = document.getElementById('performanceQualificadosChart') as HTMLCanvasElement;
    if (!ctx) {
      console.error('Canvas performanceQualificadosChart não encontrado no DOM');
      return;
    }
    if (!this.performanceListasData || !this.performanceListasData.listas) {
      console.error('Dados não disponíveis para criar gráfico de qualificados');
      return;
    }
    
    console.log('Criando gráfico de qualificados com dados:', this.performanceListasData.listas);
    
    // Destruir gráfico existente se houver
    if (this.performanceQualificadosChart) {
      this.performanceQualificadosChart.destroy();
      this.performanceQualificadosChart = null;
    }
    
    // Verificar se há um gráfico Chart.js registrado no canvas e destruí-lo
    const existingChart = Chart.getChart(ctx);
    if (existingChart) {
      existingChart.destroy();
    }
    
    const chartData: ChartData<'bar'> = {
      labels: this.performanceListasData.listas.map((l: PerformanceLista) => l.nomeLista),
      datasets: [
        {
          label: 'Qualificados',
          data: this.performanceListasData.listas.map((l: PerformanceLista) => l.totalQualificados),
          backgroundColor: 'rgba(75, 192, 192, 0.8)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
          minBarLength: 20 // Altura mínima de 20px para barras pequenas
        },
        {
          label: 'Não Qualificados',
          data: this.performanceListasData.listas.map((l: PerformanceLista) => l.totalNaoQualificados),
          backgroundColor: 'rgba(255, 99, 132, 0.8)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
          minBarLength: 20 // Altura mínima de 20px para barras pequenas
        }
      ]
    };
    
    // Plugin para garantir altura mínima nas barras pequenas
    const minBarHeightPlugin = {
      id: 'minBarHeightPluginQualificados',
      afterUpdate: (chart: Chart) => {
        const minHeight = 25; // Altura mínima em pixels
        
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (!meta || !meta.data || meta.data.length === 0) return;
          
          const yScale = (chart as any).scales.y;
          const base = yScale.getPixelForValue(0);
          
          meta.data.forEach((bar: any, index: number) => {
            if (!bar) return;
            
            const value = dataset.data[index] as number;
            if (value === 0 || value < 0) return;
            
            const y = bar.y;
            const currentHeight = Math.abs(y - base);
            
            // Se a barra for muito pequena, ajustar sua altura visual
            if (currentHeight < minHeight) {
              const direction = y < base ? 1 : -1;
              bar.height = minHeight;
              bar.y = base - (minHeight * direction);
            }
          });
        });
      }
    };
    
    // Plugin para rótulos dentro das barras (múltiplos datasets)
    const datalabelsPluginQualificados = {
      id: 'datalabelsPerformanceQualificados',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        
        ctx.save();
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          
          if (!meta || !meta.data || meta.data.length === 0) return;
          
          const base = (chart as any).scales.y.getPixelForValue(0);
          
          meta.data.forEach((bar: any, index: number) => {
            if (!bar) return;
            
            const value = dataset.data[index] as number;
            
            // Obter coordenadas da barra
            const x = bar.x;
            const y = bar.y;
            const height = Math.abs(y - base);
            
            // Usar cor branca para melhor contraste
            ctx.fillStyle = '#ffffff';
            
            // Adicionar sombra para melhor legibilidade
            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            
            // Só exibir rótulo se a barra tiver altura suficiente
            if (height > 15) {
              // Posicionar no meio da barra
              const labelY = y < base ? y + height / 2 : y - height / 2;
              ctx.fillText(value.toString(), x, labelY);
            }
          });
        });
        
        ctx.restore();
      }
    };
    
    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [minBarHeightPlugin, datalabelsPluginQualificados],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          title: {
            display: true,
            text: 'Qualificados vs Não Qualificados por Lista'
          }
        },
        scales: {
          x: {
            stacked: false
          },
          y: {
            beginAtZero: true,
            stacked: false,
            min: -0.5, // Permite que barras pequenas tenham altura mínima visual
            ticks: {
              stepSize: 1,
              precision: 0,
              callback: function(value: any) {
                // Não exibir valores negativos no eixo Y
                if (value < 0) return '';
                return value;
              }
            }
          }
        }
      }
    };
    
    try {
      this.performanceQualificadosChart = new Chart(ctx, config);
      console.log('Gráfico de qualificados criado com sucesso');
    } catch (error) {
      console.error('Erro ao criar gráfico de qualificados:', error);
    }
  }
  
  criarGraficoPerformanceTaxaQualificacao(): void {
    const ctx = document.getElementById('performanceTaxaQualificacaoChart') as HTMLCanvasElement;
    if (!ctx || !this.performanceListasData || !this.performanceListasData.listas) return;
    
    // Destruir gráfico existente se houver
    if (this.performanceTaxaQualificacaoChart) {
      this.performanceTaxaQualificacaoChart.destroy();
      this.performanceTaxaQualificacaoChart = null;
    }
    
    // Verificar se há um gráfico Chart.js registrado no canvas e destruí-lo
    const existingChart = Chart.getChart(ctx);
    if (existingChart) {
      existingChart.destroy();
    }
    
    const chartData: ChartData<'line'> = {
      labels: this.performanceListasData.listas.map((l: PerformanceLista) => l.nomeLista),
      datasets: [{
        label: 'Taxa de Qualificação (%)',
        data: this.performanceListasData.listas.map((l: PerformanceLista) => l.taxaQualificacao),
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.2)',
        borderWidth: 2,
        fill: true,
        tension: 0.4
      }]
    };
    
    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          title: {
            display: true,
            text: 'Taxa de Qualificação por Lista (%)'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `Taxa: ${context.parsed.y.toFixed(2)}%`;
              }
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: function(value) {
                return value + '%';
              }
            }
          }
        }
      }
    };
    
    this.performanceTaxaQualificacaoChart = new Chart(ctx, config);
  }
  
  criarGraficoPerformanceMensagens(): void {
    const ctx = document.getElementById('performanceMensagensChart') as HTMLCanvasElement;
    if (!ctx || !this.performanceListasData || !this.performanceListasData.listas) return;
    
    // Destruir gráfico existente se houver
    if (this.performanceMensagensChart) {
      this.performanceMensagensChart.destroy();
      this.performanceMensagensChart = null;
    }
    
    // Verificar se há um gráfico Chart.js registrado no canvas e destruí-lo
    const existingChart = Chart.getChart(ctx);
    if (existingChart) {
      existingChart.destroy();
    }
    
    const chartData: ChartData<'bar'> = {
      labels: this.performanceListasData.listas.map((l: PerformanceLista) => l.nomeLista),
      datasets: [{
        label: 'Total de Mensagens',
        data: this.performanceListasData.listas.map((l: PerformanceLista) => l.totalMensagens),
        backgroundColor: 'rgba(255, 206, 86, 0.8)',
        borderColor: 'rgba(255, 206, 86, 1)',
        borderWidth: 1
      }]
    };
    
    // Plugin para rótulos dentro das barras
    const datalabelsPluginMensagens = {
      id: 'datalabelsPerformanceMensagens',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        meta.data.forEach((bar: any, index: number) => {
          if (!bar) return;
          
          const value = chart.data.datasets[0].data[index] as number;
          
          // Obter coordenadas da barra
          const x = bar.x;
          const y = bar.y;
          const base = (chart as any).scales.y.getPixelForValue(0);
          const height = Math.abs(y - base);
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Só exibir rótulo se a barra tiver altura suficiente
          if (height > 15) {
            // Posicionar no meio da barra (y é o topo, então y - height/2 é o centro)
            const labelY = y < base ? y + height / 2 : y - height / 2;
            ctx.fillText(value.toString(), x, labelY);
          }
        });
        
        ctx.restore();
      }
    };
    
    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [datalabelsPluginMensagens],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          title: {
            display: true,
            text: 'Total de Mensagens por Lista'
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            }
          }
        }
      }
    };
    
    this.performanceMensagensChart = new Chart(ctx, config);
  }

  tentarCriarGraficoPerformanceFeiras(): void {
    const ctx = document.getElementById('performanceFeirasChart') as HTMLCanvasElement;
    if (!ctx) {
      this.tentativasCriarPerformanceFeiras++;
      if (this.tentativasCriarPerformanceFeiras < this.MAX_TENTATIVAS) {
        // Tentar novamente após um delay
        setTimeout(() => {
          this.tentarCriarGraficoPerformanceFeiras();
        }, 300);
      } else {
        console.error(`Canvas performanceFeirasChart não encontrado no DOM após ${this.MAX_TENTATIVAS} tentativas`);
        this.tentativasCriarPerformanceFeiras = 0;
      }
      return;
    }
    
    // Resetar contador se encontrou o canvas
    this.tentativasCriarPerformanceFeiras = 0;
    this.criarGraficoPerformanceFeiras();
  }

  criarGraficoPerformanceFeiras(): void {
    const ctx = document.getElementById('performanceFeirasChart') as HTMLCanvasElement;
    if (!ctx) {
      console.error('Canvas performanceFeirasChart não encontrado no DOM');
      return;
    }
    if (!this.performanceFeirasData || !this.performanceFeirasData.feiras || this.performanceFeirasData.feiras.length === 0) {
      console.error('Dados não disponíveis para criar gráfico de performance por feiras');
      return;
    }
    
    console.log('Criando gráfico de performance por feiras com dados:', this.performanceFeirasData.feiras);
    
    // Destruir gráfico existente se houver
    if (this.performanceFeirasChart) {
      this.performanceFeirasChart.destroy();
      this.performanceFeirasChart = null;
    }
    
    // Verificar se há um gráfico Chart.js registrado no canvas e destruí-lo
    const existingChart = Chart.getChart(ctx);
    if (existingChart) {
      existingChart.destroy();
    }
    
    const chartData: ChartData<'bar'> = {
      labels: this.performanceFeirasData.feiras.map((f: PerformanceFeiraResult) => f.nomeFeira),
      datasets: [
        {
          label: 'Qualificados',
          data: this.performanceFeirasData.feiras.map((f: PerformanceFeiraResult) => f.totalQualificados),
          backgroundColor: 'rgba(75, 192, 192, 0.8)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
          minBarLength: 20
        },
        {
          label: 'Não Qualificados',
          data: this.performanceFeirasData.feiras.map((f: PerformanceFeiraResult) => f.totalNaoQualificados),
          backgroundColor: 'rgba(255, 99, 132, 0.8)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1,
          minBarLength: 20
        },
        {
          label: 'Projetos',
          data: this.performanceFeirasData.feiras.map((f: PerformanceFeiraResult) => f.totalProjetos),
          backgroundColor: 'rgba(54, 162, 235, 0.8)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
          minBarLength: 20
        }
      ]
    };
    
    // Plugin para garantir altura mínima nas barras pequenas
    const minBarHeightPlugin = {
      id: 'minBarHeightPluginFeiras',
      afterUpdate: (chart: Chart) => {
        const minHeight = 25;
        
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (!meta || !meta.data || meta.data.length === 0) return;
          
          const yScale = (chart as any).scales.y;
          const base = yScale.getPixelForValue(0);
          
          meta.data.forEach((bar: any, index: number) => {
            if (!bar) return;
            
            const value = dataset.data[index] as number;
            if (value === 0 || value < 0) return;
            
            const y = bar.y;
            const currentHeight = Math.abs(y - base);
            
            if (currentHeight < minHeight) {
              const direction = y < base ? 1 : -1;
              bar.height = minHeight;
              bar.y = base - (minHeight * direction);
            }
          });
        });
      }
    };

    // Plugin para rótulos dentro das barras
    const datalabelsPluginFeiras = {
      id: 'datalabelsPerformanceFeiras',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        
        ctx.save();
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        const yScale = (chart as any).scales.y;
        const base = yScale.getPixelForValue(0);
        
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          
          if (!meta || !meta.data || meta.data.length === 0) return;
          
          meta.data.forEach((bar: any, index: number) => {
            if (!bar) return;
            
            const value = dataset.data[index] as number;
            const x = bar.x;
            const y = bar.y;
            const height = Math.abs(y - base);
            
            ctx.fillStyle = '#ffffff';
            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            
            if (height > 15 && value > 0) {
              const labelY = y < base ? y + height / 2 : y - height / 2;
              ctx.fillText(value.toString(), x, labelY);
            }
          });
        });
        
        ctx.restore();
      }
    };
    
    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [minBarHeightPlugin, datalabelsPluginFeiras],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            position: 'nearest',
            yAlign: 'bottom',
            xAlign: 'center',
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                const feira = this.performanceFeirasData!.feiras[context.dataIndex];
                const total = feira.totalQualificados + feira.totalNaoQualificados;
                
                if (label === 'Qualificados' || label === 'Não Qualificados') {
                  const percentual = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
                  return `${label}: ${value} (${percentual}% do total de contatos)`;
                } else {
                  const taxaConversao = feira.totalQualificados > 0
                    ? ((value / feira.totalQualificados) * 100).toFixed(1)
                    : '0.0';
                  return `${label}: ${value} (${taxaConversao}% dos qualificados)`;
                }
              }
            }
          },
          title: {
            display: true,
            text: 'Performance por Feira: Qualificados, Não Qualificados e Projetos'
          }
        },
        scales: {
          x: {
            stacked: false
          },
          y: {
            beginAtZero: true,
            stacked: false,
            min: -0.5,
            ticks: {
              callback: (value: any) => {
                if (value < 0) return '';
                return value;
              },
              stepSize: 1,
              precision: 0
            }
          }
        }
      }
    };
    
    try {
      this.performanceFeirasChart = new Chart(ctx, config);
      console.log('Gráfico de performance por feiras criado com sucesso');
    } catch (error) {
      console.error('Erro ao criar gráfico de performance por feiras:', error);
    }
  }

  carregarMensagensPorDiaFeira(): void {
    const hoje = new Date();
    const filtrosParaUsar = {
      tipoPeriodo: this.filtrosPerformanceListas?.tipoPeriodo || CallCenterTipoPeriodoEnum.Mes,
      mes: this.filtrosPerformanceListas?.mes || hoje.getMonth() + 1,
      ano: this.filtrosPerformanceListas?.ano || hoje.getFullYear(),
      dataInicio: this.filtrosPerformanceListas?.dataInicio || undefined,
      dataFim: this.filtrosPerformanceListas?.dataFim || undefined,
      feiraId: this.filtrosPerformanceListas?.feiraId || undefined,
      listaId: this.filtrosPerformanceListas?.listaId || undefined,
      atendente: this.filtrosPerformanceListas?.atendente || undefined
    };

    this.dashboardCallCenterService.consultarMensagensPorDiaFeira(filtrosParaUsar).subscribe({
      next: (result) => {
        console.log('Dados de Mensagens por Dia e Feira recebidos:', result);
        this.mensagensPorDiaFeiraData = result;
        
        // Forçar detecção de mudanças
        this.cdr.detectChanges();
        
        // Resetar contador de tentativas
        this.tentativasCriarMensagensPorDiaFeira = 0;
        
        // Aguardar para o Angular renderizar o canvas após o *ngIf e tentar criar o gráfico
        setTimeout(() => {
          this.tentarCriarGraficoMensagensPorDiaFeira();
        }, 100);
      },
      error: (error) => {
        console.error('Erro ao carregar mensagens por dia e feira:', error);
        this.mensagensPorDiaFeiraData = null;
      }
    });
  }

  tentarCriarGraficoMensagensPorDiaFeira(): void {
    const ctx = document.getElementById('mensagensPorDiaFeiraChart') as HTMLCanvasElement;
    if (!ctx) {
      this.tentativasCriarMensagensPorDiaFeira++;
      if (this.tentativasCriarMensagensPorDiaFeira < this.MAX_TENTATIVAS) {
        // Tentar novamente após um delay
        setTimeout(() => {
          this.tentarCriarGraficoMensagensPorDiaFeira();
        }, 300);
      } else {
        console.error(`Canvas mensagensPorDiaFeiraChart não encontrado no DOM após ${this.MAX_TENTATIVAS} tentativas`);
        this.tentativasCriarMensagensPorDiaFeira = 0;
      }
      return;
    }
    
    // Resetar contador se encontrou o canvas
    this.tentativasCriarMensagensPorDiaFeira = 0;
    this.criarGraficoMensagensPorDiaFeira();
  }

  criarGraficoMensagensPorDiaFeira(): void {
    const ctx = document.getElementById('mensagensPorDiaFeiraChart') as HTMLCanvasElement;
    if (!ctx) {
      console.error('Canvas mensagensPorDiaFeiraChart não encontrado no DOM');
      return;
    }
    
    if (!this.mensagensPorDiaFeiraData || !this.mensagensPorDiaFeiraData.feiras || this.mensagensPorDiaFeiraData.feiras.length === 0) {
      console.error('Dados não disponíveis para criar gráfico de mensagens por dia e feira');
      return;
    }
    
    console.log('Criando gráfico de mensagens por dia e feira com dados:', this.mensagensPorDiaFeiraData.feiras);
    
    // Destruir gráfico existente se houver
    if (this.mensagensPorDiaFeiraChart) {
      this.mensagensPorDiaFeiraChart.destroy();
      this.mensagensPorDiaFeiraChart = null;
    }
    
    // Verificar se há um gráfico Chart.js registrado no canvas e destruí-lo
    const existingChart = Chart.getChart(ctx);
    if (existingChart) {
      existingChart.destroy();
    }
    
    // Função para formatar datas
    const formatarData = (dataStr: string): string => {
      if (!dataStr) {
        return '';
      }
      try {
        // Se for formato YYYY-MM-DD
        if (dataStr.includes('-') && dataStr.length === 10) {
          const [ano, mes, dia] = dataStr.split('-');
          if (ano && mes && dia) {
            return `${dia}/${mes}`;
          }
        }
        // Se for formato YYYY-MM
        if (dataStr.includes('-') && dataStr.length === 7) {
          const [ano, mes] = dataStr.split('-');
          const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
          const mesIndex = parseInt(mes, 10) - 1;
          if (ano && mes && mesIndex >= 0 && mesIndex < meses.length) {
            return `${meses[mesIndex]}/${ano.substring(2)}`;
          }
        }
        return dataStr;
      } catch (error) {
        console.error('Erro ao formatar data:', error, dataStr);
        return dataStr;
      }
    };
    
    // Formatar labels de data
    const labelsFormatados = (this.mensagensPorDiaFeiraData.labels || []).map(formatarData);
    
    // Paleta de cores melhorada para feiras (cores mais distintas e acessíveis)
    const coresFeiras = [
      'rgba(54, 162, 235, 1)',   // Azul
      'rgba(255, 99, 132, 1)',   // Vermelho
      'rgba(75, 192, 192, 1)',   // Verde-água
      'rgba(255, 206, 86, 1)',   // Amarelo
      'rgba(153, 102, 255, 1)',  // Roxo
      'rgba(255, 159, 64, 1)',   // Laranja
      'rgba(201, 203, 207, 1)', // Cinza claro
      'rgba(83, 102, 255, 1)',   // Azul escuro
      'rgba(255, 99, 255, 1)',   // Magenta
      'rgba(99, 255, 132, 1)'    // Verde claro
    ];
    
    // Criar datasets (uma linha por feira)
    const datasets = this.mensagensPorDiaFeiraData.feiras.map((feira, index) => ({
      label: feira.nomeFeira,
      data: feira.mensagensPorDia,
      borderColor: coresFeiras[index % coresFeiras.length],
      backgroundColor: coresFeiras[index % coresFeiras.length].replace('1)', '0.1)'),
      fill: false,
      tension: 0.3, // Curva mais suave
      pointRadius: 3, // Pontos menores para menos poluição visual
      pointHoverRadius: 5,
      pointBackgroundColor: coresFeiras[index % coresFeiras.length],
      pointBorderColor: '#fff',
      pointBorderWidth: 1.5,
      borderWidth: 2,
      // Mostrar pontos apenas no hover para reduzir poluição
      pointHitRadius: 10
    }));
    
    const chartData: ChartData<'line'> = {
      labels: labelsFormatados,
      datasets: datasets
    };
    
    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            onClick: (e, legendItem, legend) => {
              // Permitir clicar na legenda para ocultar/mostrar séries
              if (!legendItem || legendItem.datasetIndex === undefined) {
                return;
              }
              const index = legendItem.datasetIndex;
              if (legend && legend.chart) {
                const chart = legend.chart;
                const meta = chart.getDatasetMeta(index);
                if (meta) {
                  meta.hidden = !meta.hidden;
                  chart.update();
                }
              }
            },
            labels: {
              usePointStyle: true,
              padding: 10,
              font: {
                size: 11,
                weight: '500'
              },
              boxWidth: 12,
              boxHeight: 12
            }
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: {
              size: 13,
              weight: 'bold'
            },
            bodyFont: {
              size: 12
            },
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            displayColors: true,
            callbacks: {
              title: (tooltipItems) => {
                // Formatar data completa no tooltip
                if (!tooltipItems || tooltipItems.length === 0) {
                  return '';
                }
                const firstItem = tooltipItems[0];
                if (!firstItem || firstItem.dataIndex === undefined) {
                  return '';
                }
                const index = firstItem.dataIndex;
                if (!this.mensagensPorDiaFeiraData || !this.mensagensPorDiaFeiraData.labels || index >= this.mensagensPorDiaFeiraData.labels.length) {
                  return '';
                }
                const originalLabel = this.mensagensPorDiaFeiraData.labels[index];
                try {
                  if (originalLabel && originalLabel.includes('-') && originalLabel.length === 10) {
                    const [ano, mes, dia] = originalLabel.split('-');
                    const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 
                                  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
                    const mesIndex = parseInt(mes, 10) - 1;
                    if (mesIndex >= 0 && mesIndex < meses.length) {
                      return `${dia} de ${meses[mesIndex]} de ${ano}`;
                    }
                  }
                  return originalLabel || '';
                } catch (error) {
                  console.error('Erro ao formatar data no tooltip:', error);
                  return originalLabel || '';
                }
              },
              label: (context) => {
                const label = context.dataset?.label || '';
                const value = context.parsed?.y || 0;
                return `${label}: ${value} mensagem${value !== 1 ? 's' : ''}`;
              },
              footer: (tooltipItems) => {
                if (!tooltipItems || tooltipItems.length === 0) {
                  return '';
                }
                const total = tooltipItems.reduce((sum, item) => {
                  const value = item.parsed?.y || 0;
                  return sum + value;
                }, 0);
                return `Total do dia: ${total} mensagem${total !== 1 ? 's' : ''}`;
              }
            }
          },
          title: {
            display: true,
            text: `Mensagens por Dia e por Feira | Total: ${this.mensagensPorDiaFeiraData.totalMensagens} mensagens | Média: ${this.mensagensPorDiaFeiraData.mediaMensagensPorDia.toFixed(1)} por dia`,
            font: {
              size: 14,
              weight: '600'
            },
            padding: {
              top: 8,
              bottom: 16
            },
            color: '#495057'
          }
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Período',
              font: {
                size: 12,
                weight: '500'
              },
              color: '#6c757d'
            },
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.05)'
            },
            ticks: {
              maxRotation: this.mensagensPorDiaFeiraData.labels.length > 15 ? 45 : 0,
              minRotation: this.mensagensPorDiaFeiraData.labels.length > 15 ? 45 : 0,
              font: {
                size: 10
              },
              color: '#6c757d',
              // Mostrar apenas alguns labels se houver muitos pontos
              maxTicksLimit: this.mensagensPorDiaFeiraData.labels.length > 20 ? 15 : undefined
            }
          },
          y: {
            display: true,
            title: {
              display: true,
              text: 'Quantidade de Mensagens',
              font: {
                size: 12,
                weight: '500'
              },
              color: '#6c757d'
            },
            grid: {
              display: true,
              color: 'rgba(0, 0, 0, 0.05)'
            },
            beginAtZero: true,
            ticks: {
              stepSize: this.mensagensPorDiaFeiraData.totalMensagens > 50 ? undefined : 1,
              precision: 0,
              font: {
                size: 10
              },
              color: '#6c757d'
            }
          }
        }
      }
    };
    
    try {
      this.mensagensPorDiaFeiraChart = new Chart(ctx, config);
      console.log('Gráfico de mensagens por dia e feira criado com sucesso');
    } catch (error) {
      console.error('Erro ao criar gráfico de mensagens por dia e feira:', error);
    }
  }

  carregarPerformanceAtendentes(): void {
    const hoje = new Date();
    const filtrosParaUsar = {
      tipoPeriodo: this.filtrosPerformanceListas?.tipoPeriodo || CallCenterTipoPeriodoEnum.Mes,
      mes: this.filtrosPerformanceListas?.mes || hoje.getMonth() + 1,
      ano: this.filtrosPerformanceListas?.ano || hoje.getFullYear(),
      dataInicio: this.filtrosPerformanceListas?.dataInicio || undefined,
      dataFim: this.filtrosPerformanceListas?.dataFim || undefined,
      feiraId: this.filtrosPerformanceListas?.feiraId || undefined,
      listaId: this.filtrosPerformanceListas?.listaId || undefined,
      atendente: this.filtrosPerformanceListas?.atendente || undefined
    };

    this.dashboardCallCenterService.consultarPerformanceAtendentes(filtrosParaUsar).subscribe({
      next: (result) => {
        console.log('Dados de Performance de Atendentes recebidos:', result);
        this.performanceAtendentesData = result;
        
        // Forçar detecção de mudanças
        this.cdr.detectChanges();
        
        // Resetar contador de tentativas
        this.tentativasCriarPerformanceAtendentes = 0;
        
        // Aguardar para o Angular renderizar o canvas após o *ngIf e tentar criar o gráfico
        setTimeout(() => {
          this.tentarCriarGraficoPerformanceAtendentes();
        }, 100);
      },
      error: (error) => {
        console.error('Erro ao carregar performance de atendentes:', error);
        this.performanceAtendentesData = null;
      }
    });
  }

  tentarCriarGraficoPerformanceAtendentes(): void {
    const ctx = document.getElementById('performanceAtendentesChart') as HTMLCanvasElement;
    if (!ctx) {
      this.tentativasCriarPerformanceAtendentes++;
      if (this.tentativasCriarPerformanceAtendentes < this.MAX_TENTATIVAS) {
        // Tentar novamente após um delay
        setTimeout(() => {
          this.tentarCriarGraficoPerformanceAtendentes();
        }, 300);
      } else {
        console.error(`Canvas performanceAtendentesChart não encontrado no DOM após ${this.MAX_TENTATIVAS} tentativas`);
        this.tentativasCriarPerformanceAtendentes = 0;
      }
      return;
    }
    
    // Resetar contador se encontrou o canvas
    this.tentativasCriarPerformanceAtendentes = 0;
    this.criarGraficoPerformanceAtendentes();
  }

  criarGraficoPerformanceAtendentes(): void {
    const ctx = document.getElementById('performanceAtendentesChart') as HTMLCanvasElement;
    if (!ctx) {
      console.error('Canvas performanceAtendentesChart não encontrado no DOM');
      return;
    }
    
    if (!this.performanceAtendentesData || !this.performanceAtendentesData.labels || this.performanceAtendentesData.labels.length === 0) {
      console.error('Dados não disponíveis para criar gráfico de performance de atendentes');
      return;
    }
    
    console.log('Criando gráfico de performance de atendentes com dados:', this.performanceAtendentesData);
    
    // Destruir gráfico existente se houver
    if (this.performanceAtendentesChart) {
      this.performanceAtendentesChart.destroy();
      this.performanceAtendentesChart = null;
    }
    
    // Verificar se há um gráfico Chart.js registrado no canvas e destruí-lo
    const existingChart = Chart.getChart(ctx);
    if (existingChart) {
      existingChart.destroy();
    }
    
    const chartData: ChartData<'bar'> = {
      labels: this.performanceAtendentesData.labels,
      datasets: [
        {
          label: 'Mensagens Enviadas',
          data: this.performanceAtendentesData.mensagens,
          backgroundColor: 'rgba(54, 162, 235, 0.8)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1,
          minBarLength: 20
        },
        {
          label: 'Contatos Qualificados',
          data: this.performanceAtendentesData.contatosQualificados,
          backgroundColor: 'rgba(75, 192, 192, 0.8)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1,
          minBarLength: 20
        }
      ]
    };
    
    // Plugin para garantir altura mínima nas barras pequenas
    const minBarHeightPlugin = {
      id: 'minBarHeightPluginAtendentes',
      afterUpdate: (chart: Chart) => {
        const minHeight = 25;
        
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          if (!meta || !meta.data || meta.data.length === 0) return;
          
          const yScale = (chart as any).scales.y;
          const base = yScale.getPixelForValue(0);
          
          meta.data.forEach((bar: any, index: number) => {
            if (!bar) return;
            
            const value = dataset.data[index] as number;
            if (value === 0 || value < 0) return;
            
            const y = bar.y;
            const currentHeight = Math.abs(y - base);
            
            if (currentHeight < minHeight) {
              const direction = y < base ? 1 : -1;
              bar.height = minHeight;
              bar.y = base - (minHeight * direction);
            }
          });
        });
      }
    };

    // Plugin para rótulos dentro das barras
    const datalabelsPluginAtendentes = {
      id: 'datalabelsPerformanceAtendentes',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        
        ctx.save();
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        const yScale = (chart as any).scales.y;
        const base = yScale.getPixelForValue(0);
        
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          
          if (!meta || !meta.data || meta.data.length === 0) return;
          
          meta.data.forEach((bar: any, index: number) => {
            if (!bar) return;
            
            const value = dataset.data[index] as number;
            const x = bar.x;
            const y = bar.y;
            const height = Math.abs(y - base);
            
            ctx.fillStyle = '#ffffff';
            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            
            if (height > 15 && value > 0) {
              const labelY = y < base ? y + height / 2 : y - height / 2;
              ctx.fillText(value.toString(), x, labelY);
            }
          });
        });
        
        ctx.restore();
      }
    };
    
    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [minBarHeightPlugin, datalabelsPluginAtendentes],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              usePointStyle: true,
              padding: 15,
              font: {
                size: 12
              }
            }
          },
          tooltip: {
            position: 'nearest',
            yAlign: 'bottom',
            xAlign: 'center',
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || '';
                const value = context.parsed.y;
                return `${label}: ${value}`;
              },
              footer: (tooltipItems) => {
                if (!tooltipItems || tooltipItems.length === 0) {
                  return '';
                }
                const atendenteIndex = tooltipItems[0].dataIndex;
                const mensagens = this.performanceAtendentesData!.mensagens[atendenteIndex] || 0;
                const qualificados = this.performanceAtendentesData!.contatosQualificados[atendenteIndex] || 0;
                const taxa = mensagens > 0 ? ((qualificados / mensagens) * 100).toFixed(1) : '0.0';
                return `Taxa de Qualificação: ${taxa}%`;
              }
            }
          },
          title: {
            display: true,
            text: `Performance de Atendentes | Total: ${this.performanceAtendentesData.totalMensagens} mensagens, ${this.performanceAtendentesData.totalContatosQualificados} qualificados`,
            font: {
              size: 16,
              weight: 'bold'
            },
            padding: {
              top: 10,
              bottom: 20
            }
          }
        },
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: 'Atendentes'
            },
            ticks: {
              maxRotation: 45,
              minRotation: 45,
              font: {
                size: 10
              }
            }
          },
          y: {
            display: true,
            title: {
              display: true,
              text: 'Quantidade'
            },
            beginAtZero: true,
            stacked: false,
            min: -0.5,
            ticks: {
              callback: (value: any) => {
                if (value < 0) return '';
                return value;
              },
              stepSize: 1,
              precision: 0,
              font: {
                size: 10
              }
            }
          }
        }
      }
    };
    
    try {
      this.performanceAtendentesChart = new Chart(ctx, config);
      console.log('Gráfico de performance de atendentes criado com sucesso');
    } catch (error) {
      console.error('Erro ao criar gráfico de performance de atendentes:', error);
    }
  }
  
  selecionarLista(lista: PerformanceLista): void {
    this.listaSelecionada = lista;
  }

  criarGraficoPipeline(data: PipelineProjetosResult): void {
    const ctx = document.getElementById('pipelineChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.pipelineChart) {
      this.pipelineChart.destroy();
    }

    // Processar labels para remover a parte do badge (ex: "Aprovado|badge-soft-success" -> "Aprovado")
    const labelsProcessados = data.labels.map(label => {
      const parts = label.split('|');
      return parts[0].trim();
    });

    // Cores dinâmicas baseadas no número de labels
    const cores = [
      'rgba(75, 192, 192, 0.6)',   // Verde (Aprovado)
      'rgba(255, 99, 132, 0.6)',   // Vermelho (Reprovado)
      'rgba(255, 206, 86, 0.6)',   // Amarelo (Negociando)
      'rgba(54, 162, 235, 0.6)',   // Azul
      'rgba(153, 102, 255, 0.6)'   // Roxo
    ];
    const coresBorda = [
      'rgba(75, 192, 192, 1)',
      'rgba(255, 99, 132, 1)',
      'rgba(255, 206, 86, 1)',
      'rgba(54, 162, 235, 1)',
      'rgba(153, 102, 255, 1)'
    ];

    const chartData: ChartData<'bar'> = {
      labels: labelsProcessados,
      datasets: [{
        label: 'Quantidade de Projetos',
        data: data.quantidades,
        backgroundColor: cores.slice(0, labelsProcessados.length),
        borderColor: coresBorda.slice(0, labelsProcessados.length),
        borderWidth: 1
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das barras horizontais
    const datalabelsPlugin = {
      id: 'datalabelsPipeline',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        meta.data.forEach((bar: any, index: number) => {
          if (!bar || index >= data.quantidades.length) return;
          
          const value = data.quantidades[index];
          const x = bar.x;
          const y = bar.y;
          const width = Math.abs(bar.width);
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Para barras horizontais, calcular o centro da barra
          // O x representa a posição final da barra (no eixo horizontal)
          // Para barras que crescem da esquerda para direita, o centro é x - width/2
          // Mas precisamos considerar que o x pode estar no início ou fim da barra
          const base = (chart as any).scales.x.getPixelForValue(0);
          const labelX = x > base ? x - width / 2 : x + width / 2;
          
          // Só mostrar rótulo se a barra tiver largura suficiente
          if (width > 30) {
            ctx.fillText(value.toString(), labelX, y);
          }
        });

        ctx.restore();
      }
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      options: {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `${context.parsed.x} projetos`;
              }
            }
          },
          title: {
            display: true,
            text: `Total de Projetos: ${data.totalProjetos}`
          }
        },
        scales: {
          x: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            }
          }
        }
      },
      plugins: [datalabelsPlugin]
    };

    try {
      this.pipelineChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de pipeline:', error);
    }
  }

  criarGraficoReceita(data: ReceitaTemporalResult): void {
    const ctx = document.getElementById('receitaChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.receitaChart) {
      this.receitaChart.destroy();
    }

    const chartData: ChartData<'line'> = {
      labels: data.labels,
      datasets: [{
        label: 'Receita (R$)',
        data: data.valores,
        borderColor: 'rgba(75, 192, 192, 1)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    };

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `Receita: R$ ${context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
              }
            }
          },
          title: {
            display: true,
            text: `Total: R$ ${data.totalReceita.toLocaleString('pt-BR')} | Média: R$ ${data.mediaReceita.toLocaleString('pt-BR')}`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value) => {
                return 'R$ ' + Number(value).toLocaleString('pt-BR');
              }
            }
          }
        }
      }
    };

    this.receitaChart = new Chart(ctx, config);
  }

  criarGraficoOrdens(data: StatusOrdensCompraResult): void {
    const ctx = document.getElementById('ordensChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.ordensChart) {
      this.ordensChart.destroy();
    }

    const chartData: ChartData<'pie'> = {
      labels: data.labels,
      datasets: [{
        label: 'Ordens de Compra',
        data: data.quantidades,
        backgroundColor: [
          'rgba(255, 206, 86, 0.8)',
          'rgba(75, 192, 192, 0.8)',
          'rgba(255, 99, 132, 0.8)'
        ],
        borderColor: [
          'rgba(255, 206, 86, 1)',
          'rgba(75, 192, 192, 1)',
          'rgba(255, 99, 132, 1)'
        ],
        borderWidth: 2
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das fatias do gráfico de pizza
    const datalabelsPlugin = {
      id: 'datalabelsOrdens',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        const chartArea = chart.chartArea;
        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        const radius = Math.min(chartArea.right - chartArea.left, chartArea.bottom - chartArea.top) / 2;
        const total = data.quantidades.reduce((a, b) => a + b, 0);
        
        ctx.save();
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        meta.data.forEach((arc: any, index: number) => {
          if (!arc || index >= data.quantidades.length) return;
          
          const value = data.quantidades[index];
          const percentual = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          
          // Calcular o ângulo médio da fatia
          const startAngle = arc.startAngle;
          const endAngle = arc.endAngle;
          const angle = (startAngle + endAngle) / 2;
          
          // Calcular posição do rótulo (no meio da fatia, a 60% do raio)
          const labelRadius = radius * 0.6;
          const labelX = centerX + Math.cos(angle) * labelRadius;
          const labelY = centerY + Math.sin(angle) * labelRadius;
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
          ctx.shadowBlur = 3;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Só mostrar rótulo se a fatia tiver tamanho suficiente (mais de 5% do total)
          const slicePercent = (value / total) * 100;
          
          if (slicePercent > 5) {
            // Mostrar valor e percentual
            ctx.fillText(`${value}`, labelX, labelY - 8);
            ctx.font = '12px Arial';
            ctx.fillText(`(${percentual}%)`, labelX, labelY + 8);
            ctx.font = 'bold 14px Arial'; // Restaurar fonte
          }
        });

        ctx.restore();
      }
    };

    const config: ChartConfiguration<'pie'> = {
      type: 'pie',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'right'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed || 0;
                const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                return `${label}: ${value} (${percentage}%)`;
              }
            }
          },
          title: {
            display: true,
            text: `Total de Ordens: ${data.totalOrdens}`
          }
        }
      },
      plugins: [datalabelsPlugin]
    };

    this.ordensChart = new Chart(ctx, config);
  }

  criarGraficoProjetosPorStatus(data: ProjetosPorStatusResult): void {
    const ctx = document.getElementById('projetosPorStatusChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.projetosPorStatusChart) {
      this.projetosPorStatusChart.destroy();
    }

    const chartData: ChartData<'pie'> = {
      labels: data.labels,
      datasets: [{
        label: 'Projetos por Status',
        data: data.quantidades,
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',  // Vermelho
          'rgba(54, 162, 235, 0.8)',  // Azul
          'rgba(255, 206, 86, 0.8)'   // Amarelo
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)'
        ],
        borderWidth: 2
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das fatias
    const datalabelsPlugin = {
      id: 'datalabelsProjetosPorStatus',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        const chartArea = chart.chartArea;
        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        const radius = Math.min(chartArea.right - chartArea.left, chartArea.bottom - chartArea.top) / 2;
        const total = data.quantidades.reduce((a, b) => a + b, 0);
        
        ctx.save();
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        meta.data.forEach((arc: any, index: number) => {
          if (!arc || index >= data.quantidades.length) return;
          
          const value = data.quantidades[index];
          const percentual = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          
          // Calcular o ângulo médio da fatia
          const startAngle = arc.startAngle;
          const endAngle = arc.endAngle;
          const angle = (startAngle + endAngle) / 2;
          
          // Calcular posição do rótulo (no meio da fatia, a 60% do raio)
          const labelRadius = radius * 0.6;
          const labelX = centerX + Math.cos(angle) * labelRadius;
          const labelY = centerY + Math.sin(angle) * labelRadius;
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
          ctx.shadowBlur = 3;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Só mostrar rótulo se a fatia tiver tamanho suficiente (mais de 5% do total)
          const slicePercent = (value / total) * 100;
          
          if (slicePercent > 5) {
            // Mostrar valor e percentual
            ctx.fillText(`${value}`, labelX, labelY - 8);
            ctx.font = '12px Arial';
            ctx.fillText(`(${percentual}%)`, labelX, labelY + 8);
            ctx.font = 'bold 14px Arial'; // Restaurar fonte
          }
        });
        
        ctx.restore();
      }
    };

    const config: ChartConfiguration<'pie'> = {
      type: 'pie',
      data: chartData,
      plugins: [datalabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'right'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed || 0;
                const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                const valorTotal = (data.valoresTotais && data.valoresTotais[context.dataIndex] !== undefined) 
                  ? data.valoresTotais[context.dataIndex] 
                  : 0;
                return [
                  `${label}: ${value} projetos (${percentage}%)`,
                  `Valor Total: R$ ${valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                ];
              }
            }
          },
          title: {
            display: true,
            text: `Total de Projetos: ${data.totalProjetos}`
          }
        }
      }
    };

    try {
      this.projetosPorStatusChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de projetos por status:', error);
    }
  }

  criarGraficoProjetosPorFeira(data: ProjetosPorFeiraResult): void {
    const ctx = document.getElementById('projetosPorFeiraChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.projetosPorFeiraChart) {
      this.projetosPorFeiraChart.destroy();
    }

    const chartData: ChartData<'bar'> = {
      labels: data.labels,
      datasets: [{
        label: 'Quantidade de Projetos',
        data: data.quantidades,
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das barras verticais
    const datalabelsPlugin = {
      id: 'datalabelsProjetosPorFeira',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        meta.data.forEach((bar: any, index: number) => {
          if (!bar) return;
          
          const value = chart.data.datasets[0].data[index] as number;
          
          // Obter coordenadas da barra vertical
          const x = bar.x;
          const y = bar.y;
          const base = (chart as any).scales.y.getPixelForValue(0);
          const height = Math.abs(base - y);
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Só exibir rótulo se a barra tiver altura suficiente
          if (height > 15) {
            // Para barras verticais, posicionar no topo da barra (y é o topo)
            const labelY = y + height / 2;
            ctx.fillText(value.toString(), x, labelY);
          }
        });
        
        ctx.restore();
      }
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [datalabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const quantidade = context.parsed.y;
                const valorTotal = (data.valoresTotais && data.valoresTotais[context.dataIndex] !== undefined) 
                  ? data.valoresTotais[context.dataIndex] 
                  : 0;
                return [
                  `Quantidade: ${quantidade} projetos`,
                  `Valor Total: R$ ${valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                ];
              }
            }
          },
          title: {
            display: true,
            text: `Total de Projetos: ${data.totalProjetos} | Total de Feiras: ${data.totalFeiras}`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            }
          }
        }
      }
    };

    try {
      this.projetosPorFeiraChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de projetos por feira:', error);
    }
  }

  criarGraficoProjetosTemporal(data: ProjetosTemporalResult): void {
    const ctx = document.getElementById('projetosTemporalChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.projetosTemporalChart) {
      this.projetosTemporalChart.destroy();
    }

    const chartData: ChartData<'line'> = {
      labels: data.labels,
      datasets: [{
        label: 'Quantidade de Projetos',
        data: data.quantidades,
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6
      }]
    };

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `${context.parsed.y} projetos`;
              }
            }
          },
          title: {
            display: true,
            text: `Total: ${data.totalProjetos} projetos | Média: ${data.mediaProjetos.toFixed(1)} por período`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            }
          },
          x: {
            title: {
              display: true,
              text: 'Período'
            }
          }
        }
      }
    };

    try {
      this.projetosTemporalChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de projetos temporal:', error);
    }
  }
  
  carregarResumoPerformanceLista(): void {
    if (!this.listaIdParaResumo) {
      this.resumoPerformanceLista = null;
      return;
    }
    
    this.carregandoResumo = true;
    
    // Usar filtros padrão (mês e ano atual) independentemente dos filtros do formulário
    const hoje = new Date();
    const filtros = {
      tipoPeriodo: CallCenterTipoPeriodoEnum.Mes,
      mes: hoje.getMonth() + 1,
      ano: hoje.getFullYear(),
      dataInicio: undefined,
      dataFim: undefined,
      feiraId: undefined,
      listaId: this.listaIdParaResumo,
      atendente: undefined
    };
    
    this.dashboardCallCenterService.consultarResumoPerformanceLista(filtros).subscribe({
      next: (result) => {
        this.resumoPerformanceLista = result.resumo;
        this.carregandoResumo = false;
      },
      error: (error) => {
        console.error('Erro ao carregar resumo de performance da lista:', error);
        this.resumoPerformanceLista = null;
        this.carregandoResumo = false;
        this.toastService.show('Erro ao carregar resumo da lista', {
          classname: 'bg-danger text-light',
        });
      }
    });
  }
  
  // ========== GRÁFICOS DE ORÇAMENTOS - BÁSICOS ==========
  
  criarGraficoOrcamentosPorStatus(data: OrcamentosPorStatusResult): void {
    const ctx = document.getElementById('orcamentosPorStatusChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.orcamentosPorStatusChart) {
      this.orcamentosPorStatusChart.destroy();
    }

    const chartData: ChartData<'pie'> = {
      labels: data.labels,
      datasets: [{
        label: 'Orçamentos por Status',
        data: data.quantidades,
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',  // Orçando
          'rgba(54, 162, 235, 0.8)',  // Aprovado
          'rgba(255, 206, 86, 0.8)'   // Reprovado
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)'
        ],
        borderWidth: 2
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das fatias do gráfico de pizza
    const datalabelsPlugin = {
      id: 'datalabelsOrcamentosPorStatus',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        const chartArea = chart.chartArea;
        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        const radius = Math.min(chartArea.right - chartArea.left, chartArea.bottom - chartArea.top) / 2;
        const total = data.quantidades.reduce((a, b) => a + b, 0);
        
        ctx.save();
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        meta.data.forEach((arc: any, index: number) => {
          if (!arc || index >= data.quantidades.length) return;
          
          const value = data.quantidades[index];
          const percentual = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
          
          // Calcular o ângulo médio da fatia
          const startAngle = arc.startAngle;
          const endAngle = arc.endAngle;
          const angle = (startAngle + endAngle) / 2;
          
          // Calcular posição do rótulo (no meio da fatia, a 60% do raio)
          const labelRadius = radius * 0.6;
          const labelX = centerX + Math.cos(angle) * labelRadius;
          const labelY = centerY + Math.sin(angle) * labelRadius;
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
          ctx.shadowBlur = 3;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Só mostrar rótulo se a fatia tiver tamanho suficiente (mais de 5% do total)
          const slicePercent = (value / total) * 100;
          
          if (slicePercent > 5) {
            // Mostrar valor e percentual
            ctx.fillText(`${value}`, labelX, labelY - 8);
            ctx.font = '12px Arial';
            ctx.fillText(`(${percentual}%)`, labelX, labelY + 8);
            ctx.font = 'bold 14px Arial'; // Restaurar fonte
          }
        });

        ctx.restore();
      }
    };

    const config: ChartConfiguration<'pie'> = {
      type: 'pie',
      data: chartData,
      plugins: [datalabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'right'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed || 0;
                const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(1);
                const valorTotal = (data.valoresTotais && data.valoresTotais[context.dataIndex] !== undefined) 
                  ? data.valoresTotais[context.dataIndex] 
                  : 0;
                return [
                  `${label}: ${value} (${percentage}%)`,
                  `Valor Total: R$ ${valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                ];
              }
            }
          },
          title: {
            display: true,
            text: `Total de Orçamentos: ${data.totalOrcamentos}`
          }
        }
      }
    };

    try {
      this.orcamentosPorStatusChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de orçamentos por status:', error);
    }
  }

  criarGraficoOrcamentosPorFeira(data: OrcamentosPorFeiraResult): void {
    const ctx = document.getElementById('orcamentosPorFeiraChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.orcamentosPorFeiraChart) {
      this.orcamentosPorFeiraChart.destroy();
    }

    const chartData: ChartData<'bar'> = {
      labels: data.labels,
      datasets: [{
        label: 'Quantidade de Orçamentos',
        data: data.quantidades,
        backgroundColor: 'rgba(54, 162, 235, 0.8)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das barras verticais
    const datalabelsPlugin = {
      id: 'datalabelsOrcamentosPorFeira',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        meta.data.forEach((bar: any, index: number) => {
          if (!bar) return;
          
          const value = chart.data.datasets[0].data[index] as number;
          
          const x = bar.x;
          const y = bar.y;
          const base = (chart as any).scales.y.getPixelForValue(0);
          const height = Math.abs(y - base);
          
          ctx.fillStyle = '#ffffff';
          
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          if (height > 15) {
            const labelY = y < base ? y + height / 2 : y - height / 2;
            ctx.fillText(value.toString(), x, labelY);
          }
        });
        
        ctx.restore();
      }
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [datalabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const quantidade = context.parsed.y;
                const valorTotal = (data.valoresTotais && data.valoresTotais[context.dataIndex] !== undefined) 
                  ? data.valoresTotais[context.dataIndex] 
                  : 0;
                return [
                  `Quantidade: ${quantidade} orçamentos`,
                  `Valor Total: R$ ${valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                ];
              }
            }
          },
          title: {
            display: true,
            text: `Total de Orçamentos: ${data.totalOrcamentos} | Total de Feiras: ${data.totalFeiras}`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            }
          }
        }
      }
    };

    try {
      this.orcamentosPorFeiraChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de orçamentos por feira:', error);
    }
  }

  criarGraficoOrcamentosTemporal(data: OrcamentosTemporalResult): void {
    const ctx = document.getElementById('orcamentosTemporalChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.orcamentosTemporalChart) {
      this.orcamentosTemporalChart.destroy();
    }

    const chartData: ChartData<'line'> = {
      labels: data.labels,
      datasets: [{
        label: 'Quantidade de Orçamentos',
        data: data.quantidades,
        borderColor: 'rgba(54, 162, 235, 1)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        yAxisID: 'y'
      }, {
        label: 'Receita (R$)',
        data: data.valoresTotais,
        borderColor: 'rgba(255, 99, 132, 1)',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        fill: true,
        tension: 0.4,
        pointRadius: 4,
        pointHoverRadius: 6,
        yAxisID: 'y1'
      }]
    };

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.datasetIndex === 0) {
                  return `${context.parsed.y} orçamentos`;
                } else {
                  return `Receita: R$ ${context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
                }
              }
            }
          },
          title: {
            display: true,
            text: `Total: ${data.totalOrcamentos} orçamentos | Média: ${data.mediaOrcamentos.toFixed(1)} por período | Receita Total: R$ ${data.totalReceita.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            },
            position: 'left'
          },
          y1: {
            beginAtZero: true,
            position: 'right',
            grid: {
              drawOnChartArea: false
            },
            ticks: {
              callback: (value) => {
                return 'R$ ' + Number(value).toLocaleString('pt-BR');
              }
            }
          }
        }
      }
    };

    try {
      this.orcamentosTemporalChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de orçamentos temporal:', error);
    }
  }

  // ========== GRÁFICOS DE ORÇAMENTOS - FINANCEIROS ==========
  
  criarGraficoMargemLucroDistribuicao(data: MargemLucroDistribuicaoResult): void {
    const ctx = document.getElementById('margemLucroDistribuicaoChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.margemLucroDistribuicaoChart) {
      this.margemLucroDistribuicaoChart.destroy();
    }

    const chartData: ChartData<'bar'> = {
      labels: data.labels,
      datasets: [{
        label: 'Quantidade de Orçamentos',
        data: data.quantidades,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das barras verticais
    const datalabelsPlugin = {
      id: 'datalabelsMargemLucroDistribuicao',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        meta.data.forEach((bar: any, index: number) => {
          if (!bar) return;
          
          const value = chart.data.datasets[0].data[index] as number;
          
          const x = bar.x;
          const y = bar.y;
          const base = (chart as any).scales.y.getPixelForValue(0);
          const height = Math.abs(y - base);
          
          ctx.fillStyle = '#ffffff';
          
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          if (height > 15) {
            const labelY = y < base ? y + height / 2 : y - height / 2;
            ctx.fillText(value.toString(), x, labelY);
          }
        });
        
        ctx.restore();
      }
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [datalabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const quantidade = context.parsed.y;
                const valorTotal = (data.valoresTotais && data.valoresTotais[context.dataIndex] !== undefined) 
                  ? data.valoresTotais[context.dataIndex] 
                  : 0;
                return [
                  `Quantidade: ${quantidade} orçamentos`,
                  `Valor Total: R$ ${valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                ];
              }
            }
          },
          title: {
            display: true,
            text: `Total: ${data.totalOrcamentos} orçamentos | Média: ${(data.mediaMargemLucro * 100).toFixed(1)}% | Mediana: ${(data.medianaMargemLucro * 100).toFixed(1)}%`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            }
          }
        }
      }
    };

    try {
      this.margemLucroDistribuicaoChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de margem de lucro:', error);
    }
  }

  criarGraficoCustoVsReceita(data: CustoVsReceitaResult): void {
    const ctx = document.getElementById('custoVsReceitaChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.custoVsReceitaChart) {
      this.custoVsReceitaChart.destroy();
    }

    const chartData: ChartData<'bar' | 'line'> = {
      labels: data.labels,
      datasets: [{
        label: 'Custos (R$)',
        data: data.custos,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1
      }, {
        label: 'Receitas (R$)',
        data: data.receitas,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1
      }, {
        label: 'Lucro Bruto (R$)',
        data: data.lucrosBrutos,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }, {
        label: 'Margem Lucro (%)',
        data: data.margensLucro,
        type: 'line',
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.1)',
        fill: false,
        tension: 0.4,
        yAxisID: 'y1'
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das barras verticais (apenas para datasets de barra)
    const datalabelsPlugin = {
      id: 'datalabelsCustoVsReceita',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        
        // Processar apenas os datasets de barra (índices 0, 1, 2)
        for (let datasetIndex = 0; datasetIndex < 3; datasetIndex++) {
          const meta = chart.getDatasetMeta(datasetIndex);
          
          if (!meta || !meta.data || meta.data.length === 0) continue;
          
          ctx.save();
          ctx.font = 'bold 12px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          meta.data.forEach((bar: any, index: number) => {
            if (!bar) return;
            
            const value = chart.data.datasets[datasetIndex].data[index] as number;
            
            const x = bar.x;
            const y = bar.y;
            const base = (chart as any).scales.y.getPixelForValue(0);
            const height = Math.abs(y - base);
            
            ctx.fillStyle = '#ffffff';
            
            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            
            if (height > 15) {
              const labelY = y < base ? y + height / 2 : y - height / 2;
              // Formatar valores monetários de forma compacta
              const formattedValue = value >= 1000 ? `R$ ${(value / 1000).toFixed(1)}k` : `R$ ${value.toFixed(0)}`;
              ctx.fillText(formattedValue, x, labelY);
            }
          });
          
          ctx.restore();
        }
      }
    };

    const config: ChartConfiguration<'bar' | 'line'> = {
      type: 'bar',
      data: chartData,
      plugins: [datalabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.datasetIndex === 3) {
                  return `Margem: ${context.parsed.y.toFixed(2)}%`;
                } else {
                  return `${context.dataset.label}: R$ ${context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
                }
              }
            }
          },
          title: {
            display: true,
            text: `Total Custos: R$ ${data.totalCustos.toLocaleString('pt-BR')} | Total Receita: R$ ${data.totalReceita.toLocaleString('pt-BR')} | Lucro Bruto: R$ ${data.totalLucroBruto.toLocaleString('pt-BR')} | Média Margem: ${data.mediaMargemLucro.toFixed(2)}%`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            position: 'left',
            ticks: {
              callback: (value) => {
                return 'R$ ' + Number(value).toLocaleString('pt-BR');
              }
            }
          },
          y1: {
            beginAtZero: true,
            position: 'right',
            grid: {
              drawOnChartArea: false
            },
            ticks: {
              callback: (value) => {
                return Number(value).toFixed(1) + '%';
              }
            }
          }
        }
      }
    };

    try {
      this.custoVsReceitaChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de custo vs receita:', error);
    }
  }

  criarGraficoComposicaoCustos(data: ComposicaoCustosResult): void {
    const ctx = document.getElementById('composicaoCustosChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.composicaoCustosChart) {
      this.composicaoCustosChart.destroy();
    }

    const chartData: ChartData<'bar'> = {
      labels: data.labels,
      datasets: [
        {
          label: 'Material',
          data: data.totalMaterial,
          backgroundColor: '#FF6384'
        },
        {
          label: 'Equipe/Despesa Produção',
          data: data.totalEquipeXDespesaProducao,
          backgroundColor: '#36A2EB'
        },
        {
          label: 'Custo Operacional',
          data: data.totalCustoOperacional,
          backgroundColor: '#FFCE56'
        },
        {
          label: 'Imposto Fatura Isento ISS',
          data: data.totalImpostoFaturaIsento,
          backgroundColor: '#4BC0C0'
        },
        {
          label: 'Margem de Lucro',
          data: data.totalMargemDeLucro,
          backgroundColor: '#9966FF'
        },
        {
          label: 'Comissão Vendas',
          data: data.totalComissaoVenda,
          backgroundColor: '#FF9F40'
        },
        {
          label: 'Studio Arquiteto',
          data: data.totalStudioArquiteto,
          backgroundColor: '#FF6384'
        },
        {
          label: 'Margem Negociação',
          data: data.totalMargemNegociacao,
          backgroundColor: '#C9CBCF'
        }
      ]
    };

    // Plugin customizado para adicionar rótulos dentro das barras empilhadas
    const datalabelsPlugin = {
      id: 'datalabelsComposicaoCustos',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const yScale = (chart as any).scales.y;
        const base = yScale.getPixelForValue(0);
        
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          
          if (!meta || !meta.data || meta.data.length === 0) return;
          
          ctx.save();
          ctx.font = 'bold 11px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          meta.data.forEach((bar: any, index: number) => {
            if (!bar) return;
            
            const value = dataset.data[index] as number;
            if (value === 0) return; // Não mostrar label para valores zero
            
            const x = bar.x;
            
            // Para gráficos empilhados, usar as coordenadas do Chart.js
            // bar.y é o topo da barra empilhada para este dataset
            // bar.base é a base (onde começa este segmento)
            const segmentTop = bar.y;
            const segmentBase = (bar as any).base || base;
            const segmentHeight = Math.abs(segmentTop - segmentBase);
            const segmentCenter = segmentBase + (segmentTop < segmentBase ? -segmentHeight / 2 : segmentHeight / 2);
            
            ctx.fillStyle = '#ffffff';
            
            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            
            // Só mostrar label se o segmento tiver altura suficiente
            if (segmentHeight > 15) {
              const formattedValue = value >= 1000 ? `R$ ${(value / 1000).toFixed(1)}k` : `R$ ${value.toFixed(0)}`;
              ctx.fillText(formattedValue, x, segmentCenter);
            }
          });
          
          ctx.restore();
        });
      }
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [datalabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              footer: (tooltipItems) => {
                let sum = tooltipItems.reduce((a, b) => a + (b.parsed.y || 0), 0);
                return `Total: R$ ${sum.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
              },
              label: (context) => {
                return `${context.dataset.label}: R$ ${context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
              }
            }
          },
          title: {
            display: true,
            text: `Total Geral: R$ ${data.totalGeral.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
          }
        },
        scales: {
          x: {
            stacked: true
          },
          y: {
            stacked: true,
            beginAtZero: true,
            ticks: {
              callback: (value) => {
                return 'R$ ' + Number(value).toLocaleString('pt-BR');
              }
            }
          }
        }
      }
    };

    try {
      this.composicaoCustosChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de composição de custos:', error);
    }
  }

  criarGraficoPercentuais(data: PercentuaisResult): void {
    const ctx = document.getElementById('percentuaisChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.percentuaisChart) {
      this.percentuaisChart.destroy();
    }

    const chartData: ChartData<'radar'> = {
      labels: data.labels,
      datasets: [{
        label: 'Médias',
        data: data.medias,
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        borderColor: 'rgba(54, 162, 235, 1)',
        pointBackgroundColor: 'rgba(54, 162, 235, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(54, 162, 235, 1)'
      }, {
        label: 'Medianas',
        data: data.medianas,
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        borderColor: 'rgba(255, 99, 132, 1)',
        pointBackgroundColor: 'rgba(255, 99, 132, 1)',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(255, 99, 132, 1)'
      }]
    };

    const config: ChartConfiguration<'radar'> = {
      type: 'radar',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                return `${context.dataset.label}: ${(context.parsed.r * 100).toFixed(2)}%`;
              }
            }
          },
          title: {
            display: true,
            text: `Total de Orçamentos: ${data.totalOrcamentos} | Média Margem: ${(data.mediaMargemLucro * 100).toFixed(2)}% | Desvio Padrão: ${(data.desvioPadraoMargemLucro * 100).toFixed(2)}%`
          }
        },
        scales: {
          r: {
            min: 0,
            max: 1,
            ticks: {
              stepSize: 0.1,
              callback: (value) => `${(Number(value) * 100).toFixed(0)}%`
            }
          }
        }
      }
    };

    try {
      this.percentuaisChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de percentuais:', error);
    }
  }

  criarGraficoVariacao(data: VariacaoResult): void {
    const ctx = document.getElementById('variacaoChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.variacaoChart) {
      this.variacaoChart.destroy();
    }

    const chartData: ChartData<'bar'> = {
      labels: data.labels,
      datasets: [{
        label: 'Quantidade de Orçamentos',
        data: data.quantidades,
        backgroundColor: 'rgba(153, 102, 255, 0.6)',
        borderColor: 'rgba(153, 102, 255, 1)',
        borderWidth: 1
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das barras verticais
    const datalabelsPlugin = {
      id: 'datalabelsVariacao',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.font = 'bold 13px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        meta.data.forEach((bar: any, index: number) => {
          if (!bar) return;
          
          const value = chart.data.datasets[0].data[index] as number;
          
          const x = bar.x;
          const y = bar.y;
          const base = (chart as any).scales.y.getPixelForValue(0);
          const height = Math.abs(y - base);
          
          ctx.fillStyle = '#ffffff';
          
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          if (height > 15) {
            const labelY = y < base ? y + height / 2 : y - height / 2;
            ctx.fillText(value.toString(), x, labelY);
          }
        });
        
        ctx.restore();
      }
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [datalabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const quantidade = context.parsed.y;
                const valorTotal = (data.valoresTotais && data.valoresTotais[context.dataIndex] !== undefined) 
                  ? data.valoresTotais[context.dataIndex] 
                  : 0;
                return [
                  `Quantidade: ${quantidade} orçamentos`,
                  `Valor Total: R$ ${valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                ];
              }
            }
          },
          title: {
            display: true,
            text: `Total: ${data.totalOrcamentos} orçamentos | Média: ${(data.mediaVariacao * 100).toFixed(2)}% | Mediana: ${(data.medianaVariacao * 100).toFixed(2)}%`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              stepSize: 1
            }
          }
        }
      }
    };

    try {
      this.variacaoChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de variação:', error);
    }
  }

  criarGraficoReceitaMetroQuadrado(data: ReceitaMetroQuadradoResult): void {
    const ctx = document.getElementById('receitaMetroQuadradoChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.receitaMetroQuadradoChart) {
      this.receitaMetroQuadradoChart.destroy();
    }

    const chartData: ChartData<'bar'> = {
      labels: data.labels,
      datasets: [{
        label: 'Receita por m² (R$)',
        data: data.receitaPorM2,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das barras verticais
    const datalabelsPlugin = {
      id: 'datalabelsReceitaMetroQuadrado',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        ctx.save();
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        meta.data.forEach((bar: any, index: number) => {
          if (!bar) return;
          
          const value = chart.data.datasets[0].data[index] as number;
          
          const x = bar.x;
          const y = bar.y;
          const base = (chart as any).scales.y.getPixelForValue(0);
          const height = Math.abs(y - base);
          
          ctx.fillStyle = '#ffffff';
          
          ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
          ctx.shadowBlur = 2;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          if (height > 15) {
            const labelY = y < base ? y + height / 2 : y - height / 2;
            ctx.fillText(`R$ ${value.toFixed(2)}`, x, labelY);
          }
        });
        
        ctx.restore();
      }
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [datalabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const receitaM2 = context.parsed.y;
                const valorTotal = (data.valoresTotais && data.valoresTotais[context.dataIndex] !== undefined) 
                  ? data.valoresTotais[context.dataIndex] 
                  : 0;
                const areaTotal = (data.areasTotais && data.areasTotais[context.dataIndex] !== undefined) 
                  ? data.areasTotais[context.dataIndex] 
                  : 0;
                return [
                  `Receita por m²: R$ ${receitaM2.toFixed(2)}`,
                  `Valor Total: R$ ${valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                  `Área Total: ${areaTotal.toFixed(2)} m²`
                ];
              }
            }
          },
          title: {
            display: true,
            text: `Total Receita: R$ ${data.totalReceita.toLocaleString('pt-BR')} | Total Área: ${data.totalArea.toFixed(2)} m² | Média: R$ ${data.mediaReceitaPorM2.toFixed(2)}/m² | Total Orçamentos: ${data.totalOrcamentos}`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: 'R$ por m²'
            },
            ticks: {
              callback: (value) => `R$ ${Number(value).toFixed(2)}`
            }
          }
        }
      }
    };

    try {
      this.receitaMetroQuadradoChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de receita por metro quadrado:', error);
    }
  }

  // ========== GRÁFICOS DE ORÇAMENTOS - PROCESSO ==========
  
  criarGraficoOrcamentosEnvioEmail(data: OrcamentosEnvioEmailResult): void {
    const ctx = document.getElementById('orcamentosEnvioEmailChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.orcamentosEnvioEmailChart) {
      this.orcamentosEnvioEmailChart.destroy();
    }

    const chartData: ChartData<'line'> = {
      labels: data.labels,
      datasets: [{
        label: 'Enviados',
        data: data.quantidadesEnviados,
        backgroundColor: 'rgba(75, 192, 192, 0.6)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4
      }, {
        label: 'Não Enviados',
        data: data.quantidadesNaoEnviados,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4
      }, {
        label: 'Taxa Envio (%)',
        data: data.taxaEnvio,
        type: 'line',
        borderColor: 'rgba(153, 102, 255, 1)',
        backgroundColor: 'rgba(153, 102, 255, 0.1)',
        fill: false,
        tension: 0.4,
        yAxisID: 'y1'
      }]
    };

    const config: ChartConfiguration<'line'> = {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          intersect: false,
          mode: 'index'
        },
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.datasetIndex === 2) {
                  return `Taxa: ${context.parsed.y.toFixed(2)}%`;
                } else {
                  return `${context.dataset.label}: ${context.parsed.y} orçamentos`;
                }
              }
            }
          },
          title: {
            display: true,
            text: `Total Enviados: ${data.totalEnviados} | Total Não Enviados: ${data.totalNaoEnviados} | Taxa Geral: ${data.taxaEnvioGeral.toFixed(2)}% | Total: ${data.totalOrcamentos}`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            position: 'left',
            ticks: {
              stepSize: 1
            }
          },
          y1: {
            beginAtZero: true,
            max: 100,
            position: 'right',
            grid: {
              drawOnChartArea: false
            },
            ticks: {
              callback: (value) => Number(value).toFixed(0) + '%'
            }
          }
        }
      }
    };

    try {
      this.orcamentosEnvioEmailChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de envio de email:', error);
    }
  }

  criarGraficoOrcamentosPorEvento(data: OrcamentosPorEventoResult): void {
    const ctx = document.getElementById('orcamentosPorEventoChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.orcamentosPorEventoChart) {
      this.orcamentosPorEventoChart.destroy();
    }

    const chartData: ChartData<'bar'> = {
      labels: data.labels,
      datasets: [{
        label: 'Quantidade',
        data: data.quantidades,
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
        borderColor: 'rgba(54, 162, 235, 1)',
        borderWidth: 1,
        yAxisID: 'y'
      }, {
        label: 'Receita (R$)',
        data: data.valoresTotais,
        backgroundColor: 'rgba(255, 99, 132, 0.6)',
        borderColor: 'rgba(255, 99, 132, 1)',
        borderWidth: 1,
        yAxisID: 'y1'
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das barras verticais
    const datalabelsPlugin = {
      id: 'datalabelsOrcamentosPorEvento',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        
        // Processar ambos os datasets
        chart.data.datasets.forEach((dataset, datasetIndex) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          
          if (!meta || !meta.data || meta.data.length === 0) return;
          
          ctx.save();
          ctx.font = 'bold 12px Arial';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          
          const base = datasetIndex === 0 
            ? (chart as any).scales.y.getPixelForValue(0)
            : (chart as any).scales.y1.getPixelForValue(0);
          
          meta.data.forEach((bar: any, index: number) => {
            if (!bar) return;
            
            const value = dataset.data[index] as number;
            
            const x = bar.x;
            const y = bar.y;
            const height = Math.abs(y - base);
            
            ctx.fillStyle = '#ffffff';
            
            ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;
            
            if (height > 15) {
              const labelY = y < base ? y + height / 2 : y - height / 2;
              if (datasetIndex === 0) {
                ctx.fillText(value.toString(), x, labelY);
              } else {
                const formattedValue = value >= 1000 ? `R$ ${(value / 1000).toFixed(1)}k` : `R$ ${value.toFixed(0)}`;
                ctx.fillText(formattedValue, x, labelY);
              }
            }
          });
          
          ctx.restore();
        });
      }
    };

    const config: ChartConfiguration<'bar'> = {
      type: 'bar',
      data: chartData,
      plugins: [datalabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'top'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                if (context.datasetIndex === 0) {
                  return `Quantidade: ${context.parsed.y} orçamentos`;
                } else {
                  return `Receita: R$ ${context.parsed.y.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
                }
              }
            }
          },
          title: {
            display: true,
            text: `Total Orçamentos: ${data.totalOrcamentos} | Receita Total: R$ ${data.totalReceita.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            position: 'left',
            ticks: {
              stepSize: 1
            }
          },
          y1: {
            beginAtZero: true,
            position: 'right',
            grid: {
              drawOnChartArea: false
            },
            ticks: {
              callback: (value) => {
                return 'R$ ' + Number(value).toLocaleString('pt-BR');
              }
            }
          }
        }
      }
    };

    try {
      this.orcamentosPorEventoChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de orçamentos por evento:', error);
    }
  }

  criarGraficoAprovacaoOrcamento(data: AprovacaoOrcamentoResult): void {
    const ctx = document.getElementById('aprovacaoOrcamentoChart') as HTMLCanvasElement;
    if (!ctx) return;

    if (this.aprovacaoOrcamentoChart) {
      this.aprovacaoOrcamentoChart.destroy();
    }

    const chartData: ChartData<'pie'> = {
      labels: data.labels,
      datasets: [{
        label: 'Aprovação de Orçamentos',
        data: data.quantidades,
        backgroundColor: [
          'rgba(255, 99, 132, 0.8)',  // Orçando
          'rgba(54, 162, 235, 0.8)',  // Aprovado
          'rgba(255, 206, 86, 0.8)'   // Reprovado
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)',
          'rgba(255, 206, 86, 1)'
        ],
        borderWidth: 2
      }]
    };

    // Plugin customizado para adicionar rótulos dentro das fatias do gráfico de pizza
    const datalabelsPlugin = {
      id: 'datalabelsAprovacaoOrcamento',
      afterDraw: (chart: Chart) => {
        const ctx = chart.ctx;
        const meta = chart.getDatasetMeta(0);
        
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        const chartArea = chart.chartArea;
        const centerX = (chartArea.left + chartArea.right) / 2;
        const centerY = (chartArea.top + chartArea.bottom) / 2;
        const radius = Math.min(chartArea.right - chartArea.left, chartArea.bottom - chartArea.top) / 2;
        const total = data.quantidades.reduce((a, b) => a + b, 0);
        
        ctx.save();
        ctx.font = 'bold 14px Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        meta.data.forEach((arc: any, index: number) => {
          if (!arc || index >= data.quantidades.length) return;
          
          const value = data.quantidades[index];
          const percentual = total > 0 ? ((value / total) * 100).toFixed(2) : 0;
          
          // Calcular o ângulo médio da fatia
          const startAngle = arc.startAngle;
          const endAngle = arc.endAngle;
          const angle = (startAngle + endAngle) / 2;
          
          // Calcular posição do rótulo (no meio da fatia, a 60% do raio)
          const labelRadius = radius * 0.6;
          const labelX = centerX + Math.cos(angle) * labelRadius;
          const labelY = centerY + Math.sin(angle) * labelRadius;
          
          // Usar cor branca para melhor contraste
          ctx.fillStyle = '#ffffff';
          
          // Adicionar sombra para melhor legibilidade
          ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
          ctx.shadowBlur = 3;
          ctx.shadowOffsetX = 1;
          ctx.shadowOffsetY = 1;
          
          // Só mostrar rótulo se a fatia tiver tamanho suficiente (mais de 5% do total)
          const slicePercent = (value / total) * 100;
          
          if (slicePercent > 5) {
            // Mostrar valor e percentual
            ctx.fillText(`${value}`, labelX, labelY - 8);
            ctx.font = '12px Arial';
            ctx.fillText(`(${percentual}%)`, labelX, labelY + 8);
            ctx.font = 'bold 14px Arial'; // Restaurar fonte
          }
        });

        ctx.restore();
      }
    };

    const config: ChartConfiguration<'pie'> = {
      type: 'pie',
      data: chartData,
      plugins: [datalabelsPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: 'bottom'
          },
          tooltip: {
            callbacks: {
              label: (context) => {
                const label = context.label || '';
                const value = context.parsed || 0;
                const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
                const percentage = ((value / total) * 100).toFixed(2);
                const valorTotal = (data.valoresTotais && data.valoresTotais[context.dataIndex] !== undefined) 
                  ? data.valoresTotais[context.dataIndex] 
                  : 0;
                return [
                  `${label}: ${value} (${percentage}%)`,
                  `Valor Total: R$ ${valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                ];
              }
            }
          },
          title: {
            display: true,
            text: `Total: ${data.totalOrcamentos} | Aprovados: ${data.totalAprovados} (${data.taxaAprovacao.toFixed(2)}%) | Reprovados: ${data.totalReprovados} (${data.taxaReprovacao.toFixed(2)}%) | Orçando: ${data.totalOrcando}`
          }
        }
      }
    };

    try {
      this.aprovacaoOrcamentoChart = new Chart(ctx, config);
    } catch (error) {
      console.error('Erro ao criar gráfico de aprovação:', error);
    }
  }

  formatarPercentual(valor: number): string {
    return `${valor.toFixed(1)}%`;
  }
  
  formatarDecimal(valor: number): string {
    return valor.toFixed(2);
  }

  verificarPermissoes(): void {
    // Verifica se o usuário tem permissão de Editar OU Ver em Adm_Usuarios
    this.temAcessoAoDashboard =
      this.authService.verificarPermissao('Adm_Usuarios', 'Ver') &&
      this.authService.verificarPermissao('Adm_Usuarios', 'Editar');
  }

  carregarProximosEventos(): void {
    // Consultar projetos com status 1
    this.dashboardService.consultarProjetosPorListaStatus([1]).subscribe({
      next: (response) => {
        if (response && response.projetos) {
          this.transactionsAll = response.projetos.map(projeto => ({
            nomeFeira: projeto.nomeFeira,
            dataInicioFeira: this.formatarData(projeto.dataInicioFeira),
            dataFimFeira: this.formatarData(projeto.dataFimFeira)
          }));
        } else {
          this.transactionsAll = [];
        }
      },
      error: (error) => {
        console.error('Erro ao carregar próximos eventos:', error);
        this.transactionsAll = [];
        this.toastService.show('Erro ao carregar próximos eventos', {
          classname: 'bg-danger text-light',
        });
      }
    });
  }

  formatarData(data: string): string {
    if (!data) return '';
    try {
      const date = new Date(data);
      const dia = date.getDate().toString().padStart(2, '0');
      const mes = (date.getMonth() + 1).toString().padStart(2, '0');
      const ano = date.getFullYear();
      return `${dia}/${mes}/${ano}`;
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return data;
    }
  }
}
